// docs/92 HM6, docs/94, docs/95, docs/116: what a commissioned huge body wears on the board, inside its
// UnitSprite (it walks, lunges and falls with the figure).
//
// - The ground under it: a shader at art resolution, refreshed at 20 Hz like the weather's
//   (docs/20) — a red dragon's molten, flickering cracks (added light), a mountain troll's split
//   and dusty earth, a yeti's creeping rime, a sandworm's churning sand, the spider queen's web, a
//   basilisk's swamp pool, a black dragon's pool of darkness with embers under it, a great eagle's
//   shadow and the wind, an ent's mossy floor and creeping roots, a grim giant's frozen flagstones
//   and circle of runes, the kraken's churning deep water with a foam ring and an ink swirl, the
//   maw's black abyssal water with a violet glow pulsing in it.
// - The air round it: the dragon's back-glow breathing with it and embers rising off its scales;
//   the troll's dust stirring at its feet and grit falling off its hide; snow flurries, glints in
//   its pelt and puffs of icy breath round the yeti; sand pouring off the worm and spouting up round
//   it; the queen's runes pulsing red on her back and on a circle under her while spiderlings skitter
//   out; mist, marsh-lights and dripping venom round the basilisk, its eye glinting; black smoke,
//   dull embers, falling ash and a darkness pooling round the black dragon, its red eye in it;
//   wind streaks and drifting feathers round the eagle as it hovers and bobs; leaves falling,
//   fireflies and pollen round the ent, its eyes glowing green; snow, cold breath steaming from the
//   giant's beard and runes glinting on its belt; foam churning round the kraken, bubbles rising,
//   ink swirling under it, its two red eyes glowing and its arm tips breaking the water; the maw's
//   violet glow, teeth glinting at its mouth, brine drooling off it, its many orange eyes winking
//   and a ring of black water round it (docs/116).
// - The figure breathes: a slow swell of its chest.
// - Its moments, each played from the board (`HugeFx` in the scene picks which, `moments.ts`).
//
// Every point on the art (a head, a mouth, an eye) is given in the image as drawn, from its feet
// point; most of the art faces left, and `HugeArt.faces` says when one does not (docs/95).
//
// Reduce motion keeps the glows and the ground, still, and plays no particles; the effect-intensity
// setting thins every emitter.
import Phaser from 'phaser';
import { TEX, ensureSpellTextures } from '../spells/textures';
import { auraQuality, type AuraQuality } from '../status/StatusAura';
import { bodyOriginY, type HugeArt, type HugeKind } from '../hugeArt';
import { HUGE_GROUND_FRAG } from './hugeGround';
import { playSfx } from '../../core/audio';
import { hugeSpace } from '../../core/audio/hall';
import type { HugeMoment } from './moments';
import { oneShot } from '../board/oneShots';

export { momentOf, type HugeMoment } from './moments';

const ADD = Phaser.BlendModes.ADD;
const NORMAL = Phaser.BlendModes.NORMAL;

export interface HugeHost {
  scene: Phaser.Scene;
  container: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Image;
  /** The body and its outline copies (they breathe together). */
  bodies: () => Phaser.GameObjects.Image[];
  cell: number;
  /** Squares a side. */
  size: number;
  art: HugeArt;
  unitId: number;
}

type Pt = { x: number; y: number };
type EmitterCfg = Phaser.Types.GameObjects.Particles.ParticleEmitterConfig;

/** Each kind's colours, breath and ground (the shader's `uKind`, and how it is laid over the tiles). */
interface Look {
  /** A roar's and a landing's ring. */
  ring: number;
  /** Dust, spray or smoke it kicks up. */
  dust: number[];
  /** What jumps when it strikes the ground. */
  shard: number[];
  /** One breath, in ms of the sine's period. */
  breath: number;
  uKind: number;
  /** The ground is light added (the dragon) or drawn over the tiles. */
  additive: boolean;
  /** Where its head (its mouth, its beak) is drawn, as fractions of the art as drawn from its feet point. */
  head: Pt;
}

const LOOKS: Readonly<Record<HugeKind, Look>> = {
  dragon: { ring: 0x9a6a4a, dust: [0x9a8a72, 0x7a6c58], shard: [0x7d7468, 0x5b5348], breath: 900, uKind: 0, additive: true, head: { x: -0.36, y: -0.62 } },
  troll: { ring: 0x8a7a62, dust: [0x9a8a72, 0x7a6c58], shard: [0x7d7468, 0x5b5348, 0x9c917f], breath: 1300, uKind: 1, additive: false, head: { x: -0.1, y: -0.8 } },
  yeti: { ring: 0xdff2ff, dust: [0xf4faff, 0xd8ecfa, 0xb8d8f0], shard: [0xe8f6ff, 0xa8d8f4, 0x7ab8e0], breath: 1100, uKind: 2, additive: false, head: { x: -0.19, y: -0.66 } },
  worm: { ring: 0xc8a874, dust: [0xc8a874, 0xa88a5a, 0xe0c690], shard: [0x8a6a44, 0x6a5034, 0xb08c5c], breath: 1700, uKind: 3, additive: false, head: { x: -0.24, y: -0.7 } },
  spider: { ring: 0xc01830, dust: [0x4a3a44, 0x2a2028, 0x6a5a60], shard: [0x2a1a22, 0x4a2a30, 0x6a4048], breath: 1000, uKind: 4, additive: false, head: { x: -0.08, y: -0.36 } },
  serpent: { ring: 0x9aa860, dust: [0x6a7a4a, 0x4a5a34, 0x8a9a6a], shard: [0x8a8a80, 0x6a6a62, 0xa8a89c], breath: 1600, uKind: 5, additive: false, head: { x: -0.25, y: -0.66 } },
  wyrm: { ring: 0x5a1410, dust: [0x2a2228, 0x1a141a, 0x3a3034], shard: [0x2a2228, 0x4a3a3a, 0x6a2a1a], breath: 1200, uKind: 6, additive: false, head: { x: -0.4, y: -0.44 } },
  // docs/95: the great eagle (its shards are feathers), the ent (splinters), the grim giant (stone and ice)
  eagle: { ring: 0xeef2f4, dust: [0xe8e6dc, 0xcfccc0, 0xb8b4a6], shard: [0x5a3a1e, 0x7a5230, 0xf0ead8], breath: 1000, uKind: 7, additive: false, head: { x: -0.29, y: -0.34 } },
  ent: { ring: 0x6a8a3a, dust: [0x7a6a48, 0x5a4a30, 0x8a7a58], shard: [0x6a4a2a, 0x8a6a3a, 0x4a321a], breath: 2000, uKind: 8, additive: false, head: { x: -0.08, y: -0.6 } },
  giant: { ring: 0xbfe6ff, dust: [0xf0f8ff, 0xd0e2f0, 0xb0c4d4], shard: [0x8a8e96, 0x6a6e76, 0xb8c8d8], breath: 1500, uKind: 9, additive: false, head: { x: -0.08, y: -0.76 } },
  // docs/116: the kraken (its dust is spray, its shards water and broken shell), the maw (teeth and brine)
  kraken: { ring: 0xc8f0f4, dust: [0xe8f8fa, 0xb8e0e8, 0x7aaab8], shard: [0xd8f4fa, 0x8ac8d8, 0x4a6a7a], breath: 2400, uKind: 10, additive: false, head: { x: -0.03, y: -0.46 } },
  maw: { ring: 0x8a3ad0, dust: [0x3a2a48, 0x241830, 0x5a4a6a], shard: [0xeee2c4, 0xc8b894, 0x6a3a5a], breath: 1400, uKind: 11, additive: false, head: { x: -0.17, y: -0.56 } },
};

/** Spider queen: where the runes on her back sit. */
const ABDOMEN: Pt = { x: 0.18, y: -0.7 };
/** Basilisk, black dragon, eagle, ent and giant: where the eye that glints sits. */
const EYE: Partial<Record<HugeKind, Pt>> = {
  serpent: { x: -0.23, y: -0.69 }, wyrm: { x: -0.35, y: -0.5 },
  eagle: { x: -0.19, y: -0.41 }, ent: { x: -0.08, y: -0.66 }, giant: { x: -0.05, y: -0.84 },
  // docs/116: the kraken's left red eye (`KRAKEN_EYE` is its other), the brightest of the maw's eyes
  kraken: { x: -0.02, y: -0.56 }, maw: { x: 0.06, y: -0.66 },
};
/** The grim giant: its rune club's head and the runes on its belt (docs/95). */
const CLUB: Pt = { x: -0.35, y: -0.82 };
const BELT: Pt = { x: 0.02, y: -0.5 };
/** The ent: the hand it hurls a log from. */
const BOUGH: Pt = { x: -0.29, y: -0.32 };
/** The kraken (docs/116): its other red eye, and the tips of the arms sprawled round it (as drawn). */
const KRAKEN_EYE: Pt = { x: 0.05, y: -0.58 };
const ARM_TIPS: readonly Pt[] = [
  { x: -0.44, y: -0.3 }, { x: -0.27, y: -0.74 }, { x: 0.36, y: -0.66 }, { x: 0.44, y: -0.2 }, { x: -0.2, y: -0.06 }, { x: 0.22, y: -0.04 },
];
/** The maw: the middle of its mouth, and the patch of its back the small eyes cover. */
const MAW_MOUTH: Pt = { x: -0.18, y: -0.55 };
const MAW_BACK: Pt = { x: 0.1, y: -0.68 };

const VENOM = [0xd8ff7a, 0x9cff4a, 0x5fc824, 0x2f7a12];
const SILK = [0xffffff, 0xe8e8f4, 0xc8c8d8];
const BLACKFIRE = [0x2a0a24, 0x1a0814, 0x120610, 0x060306];
const BLACKFIRE_CORE = [0xd04a2a, 0x9a1c3a, 0x5a0a2a, 0x200410];
const FEATHERS = [0x5a3a1e, 0x7a5230, 0x4a2e18, 0xf0ead8];
const LEAVES = [0x6a9a2a, 0x8ab83a, 0x4a7a1e, 0xb8a03a, 0xa8642a];
const RUNE = 0x8ad8ff;
const FOAM = [0xffffff, 0xe8f8fa, 0xc8eef4];
const BRINE = [0x8ab8c8, 0x5a8a9a, 0xc8e8f0];
const INK = [0x06080e, 0x0e1018, 0x141020, 0x1a1a2a];
const ABYSS = 0x9a4aff;
const DROOL = [0xb898c8, 0x8a6aa0, 0x6a4a7a, 0xc8d8c0];
const EYES = [0xffb02a, 0xff7a1a, 0xff4a1a];

let serial = 0;

export class HugeAura {
  private readonly parts: Phaser.GameObjects.GameObject[] = [];
  private readonly emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  private readonly timers: Phaser.Time.TimerEvent[] = [];
  /** Parts that sit on the art (an eye, a mouth, a back) and move to its other side when it turns. */
  private readonly anchored: Array<{ obj: { x: number; y: number }; at: Pt; dy?: number }> = [];
  private shader?: Phaser.GameObjects.Shader;
  private ground?: Phaser.GameObjects.Image;
  private glow?: Phaser.GameObjects.Image;
  /** A second light that pulses on its own beat (the queen's rune circle, an eye). */
  private spark?: Phaser.GameObjects.Image;
  /** The black dragon's darkness, pooled round it (the kraken's ink, the maw's black water). */
  private shade?: Phaser.GameObjects.Image;
  /** docs/116: the kraken's other red eye, and the ring of foam round it. */
  private twin?: Phaser.GameObjects.Image;
  private foam?: Phaser.GameObjects.Image;
  private glowBase = 0.22;
  /** Lights flaring in a moment (the breathing leaves them be). */
  private readonly held = new Set<Phaser.GameObjects.Image>();
  private texKey?: string;
  private lastRender = -1;
  private time = 0;
  private dead = false;
  private down = false;
  private readonly onUpdate = (t: number) => this.update(t);
  /** The footprint's side in world pixels. */
  private readonly side: number;

  constructor(private readonly host: HugeHost, private readonly q: AuraQuality = auraQuality()) {
    ensureSpellTextures(host.scene);
    this.side = host.cell * host.size;
    this.build();
    host.scene.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    host.container.once(Phaser.GameObjects.Events.DESTROY, () => this.destroy());
  }

  get kind(): HugeArt['kind'] { return this.host.art.kind; }

  private get look(): Look { return LOOKS[this.kind]; }

  // ---- standing look ---------------------------------------------------------------------------

  private build(): void {
    const { scene, container } = this.host;
    const look = this.look;
    const side = this.side;
    // the ground: a quad half again the footprint, centred under it (the feet point is its bottom middle)
    if (scene.game.renderer.type === Phaser.WEBGL) {
      const px = Math.round(side * (this.kind === 'spider' || this.kind === 'wyrm' || this.kind === 'kraken' || this.kind === 'maw' ? 1.8 : 1.6));
      const key = `fk-huge-ground-${++serial}`;
      const frozen = (this.host.unitId * 7) % 40;
      const s = new Phaser.GameObjects.Shader(scene, {
        name: `fkHugeGround${look.uKind}`,
        fragmentSource: HUGE_GROUND_FRAG,
        setupUniforms: (set: (name: string, value: unknown) => void) => {
          set('uSize', [px, px]);
          set('uTime', this.q.still ? frozen : this.time / 1000);
          set('uKind', look.uKind);
          set('uFade', this.down ? 0 : 1);
        },
      }, 0, 0, px, px);
      s.setRenderToTexture(key);
      s.renderImmediate();
      this.shader = s; this.texKey = key;
      this.ground = scene.add.image(0, -side / 2, key).setDisplaySize(px, px * 0.72).setBlendMode(look.additive ? ADD : NORMAL).setAlpha(look.additive ? 0.9 : 0.85);
      container.addAt(this.ground, 1);
      this.parts.push(this.ground);
    }
    switch (this.kind) {
      case 'dragon': return this.buildDragon();
      case 'troll': return this.buildTroll();
      case 'yeti': return this.buildYeti();
      case 'worm': return this.buildWorm();
      case 'spider': return this.buildSpider();
      case 'serpent': return this.buildSerpent();
      case 'wyrm': return this.buildWyrm();
      case 'eagle': return this.buildEagle();
      case 'ent': return this.buildEnt();
      case 'giant': return this.buildGiant();
      case 'kraken': return this.buildKraken();
      case 'maw': return this.buildMaw();
    }
  }

  private buildDragon(): void {
    const side = this.side;
    // the back-glow: a hot breath of light behind the figure, swelling as it breathes
    this.glow = this.light(0, -side * 0.55, side * 1.5, side * 1.3, 0xff5a14, 0.35, true);
    // embers rising off its scales, and a few sparks spat higher
    this.emit(TEX.flame, {
      lifespan: { min: 700, max: 1300 }, speedY: { min: -36, max: -14 }, speedX: { min: -8, max: 8 }, scale: { start: 0.9, end: 0.1 }, alpha: { start: 0.95, end: 0 },
      color: [0xfff1a0, 0xffb02e, 0xf05a14, 0x6a1408], colorEase: 'Quad.easeOut', blendMode: ADD, frequency: 110,
      emitZone: this.zone(-side * 0.4, -side * 0.95, side * 0.8, side * 0.8),
    });
    this.emit(TEX.px, {
      lifespan: { min: 900, max: 1600 }, speedY: { min: -60, max: -30 }, speedX: { min: -18, max: 18 }, scale: { start: 0.9, end: 0 }, color: [0xffffff, 0xffd060, 0xff6a1a], blendMode: ADD, frequency: 260,
      emitZone: this.zone(-side * 0.35, -side * 0.9, side * 0.7, side * 0.5),
    });
  }

  private buildTroll(): void {
    const side = this.side;
    // dust stirring at its feet, and grit trickling off its hide
    this.emit(TEX.smoke, {
      lifespan: { min: 1200, max: 2000 }, speedX: { min: -14, max: 14 }, speedY: { min: -6, max: -1 }, scale: { start: 0.4, end: 0.9 }, alpha: { start: 0.35, end: 0 },
      tint: [0x8a7a62, 0x6a5c48, 0xa89878], blendMode: NORMAL, frequency: 340,
      emitZone: this.zone(-side * 0.5, -4, side, 4),
    });
    this.emit(TEX.px, {
      lifespan: { min: 500, max: 800 }, speedY: { min: 10, max: 30 }, speedX: { min: -4, max: 4 }, gravityY: 120, scale: { start: 0.7, end: 0.5 }, alpha: { start: 1, end: 0.4 },
      tint: [0x7d7468, 0x5b5348, 0x9c917f], blendMode: NORMAL, frequency: 520,
      emitZone: this.zone(-side * 0.3, -side * 1.1, side * 0.6, side * 0.6),
    });
  }

  /** A yeti: a cold breath of light, snow flurrying round it on a mountain wind, glints in its pelt, icy breath. */
  private buildYeti(): void {
    const side = this.side;
    this.glow = this.light(0, -side * 0.5, side * 1.7, side * 1.4, 0x9fd8ff, 0.2, true);
    this.glowBase = 0.12;
    // flurries: flakes tumbling down and sideways on the wind, over and round it
    this.emit(TEX.flake, {
      lifespan: { min: 1600, max: 2600 }, speedY: { min: 8, max: 22 }, speedX: { min: -18, max: 4 }, rotate: { min: 0, max: 360 }, scale: { min: 0.4, max: 0.9 }, alpha: { start: 0.95, end: 0 },
      tint: [0xffffff, 0xeaf6ff, 0xc8e4fa], blendMode: NORMAL, frequency: 120,
      emitZone: this.zone(-side * 1.0, -side * 1.6, side * 2.0, side * 0.9),
    });
    // glints: rime catching the light in its pelt
    this.emit(TEX.px, {
      lifespan: { min: 260, max: 520 }, scale: { start: 1, end: 0 }, alpha: { start: 1, end: 0 }, color: [0xffffff, 0xcff0ff], blendMode: ADD, frequency: 240,
      emitZone: this.zone(-side * 0.4, -side * 1.0, side * 0.8, side * 0.9),
    });
    // icy breath: a puff from its maw at the top of each breath, drifting forward and up
    this.emitAt(this.look.head, TEX.smoke, {
      lifespan: { min: 700, max: 1100 }, speedX: { min: -12, max: 12 }, speedY: { min: -12, max: -4 }, scale: { start: 0.25, end: 0.9 }, alpha: { start: 0.55, end: 0 },
      tint: [0xf4faff, 0xd8ecfa], blendMode: NORMAL, frequency: this.look.breath * 2 * Math.PI, quantity: 5,
    });
  }

  /** A sandworm: sand pouring off its hide, spouting up round it, a haze of grit at its base. */
  private buildWorm(): void {
    const side = this.side;
    this.emit(TEX.px, {
      lifespan: { min: 600, max: 1000 }, speedY: { min: 0, max: 20 }, speedX: { min: -6, max: 6 }, gravityY: 170, scale: { min: 0.5, max: 0.9 }, alpha: { start: 1, end: 0.5 },
      tint: this.look.dust, blendMode: NORMAL, frequency: 70,
      emitZone: this.zone(-side * 0.45, -side * 0.95, side * 0.7, side * 0.55),
    });
    this.emit(TEX.smoke, {
      lifespan: { min: 1200, max: 2000 }, speedX: { min: -18, max: 18 }, speedY: { min: -5, max: -1 }, scale: { start: 0.5, end: 1.2 }, alpha: { start: 0.32, end: 0 },
      tint: [0xc8a874, 0xb0905e, 0xd8bc88], blendMode: NORMAL, frequency: 260,
      emitZone: this.zone(-side * 0.6, -6, side * 1.2, 6),
    });
    // spouts: every so often the sand round it heaves and throws a plume up
    this.every(700, () => {
      const a = Math.random() * Math.PI * 2, d = side * (0.5 + Math.random() * 0.35);
      this.spout(Math.cos(a) * d, -side * 0.45 + Math.sin(a) * d * 0.36, 0.6);
    });
  }

  /** The spider queen: runes pulsing red on her back and on a circle under her, spiderlings, drifting silk. */
  private buildSpider(): void {
    const side = this.side;
    const { scene } = this.host;
    // a summoning circle on her web, turning slowly, red
    this.spark = scene.add.image(0, -side * 0.45, TEX.rune).setTint(0xd01830).setBlendMode(ADD).setAlpha(0.3);
    this.spark.setDisplaySize(side * 1.25, side * 1.25 * 0.5);
    this.under(this.spark);
    this.parts.push(this.spark);
    // her back's runes: a light over them that beats like a heart
    this.glow = this.light(0, 0, side * 0.55, side * 0.42, 0xff2040, 0.3, false);
    this.anchored.push({ obj: this.glow, at: ABDOMEN });
    this.glowBase = 0.12;
    // spiderlings skittering out from under her and away
    this.emit(TEX.dot, {
      lifespan: { min: 500, max: 1000 }, speed: { min: 22, max: 48 }, angle: { min: 0, max: 360 }, scale: { min: 0.7, max: 1 }, alpha: { start: 1, end: 0, ease: 'Expo.easeIn' },
      tint: [0x140c12, 0x2a1420, 0x3a0c14], blendMode: NORMAL, frequency: 200,
      emitZone: this.zone(-side * 0.4, -side * 0.25, side * 0.8, side * 0.2),
    });
    // silk motes drifting up off her
    this.emit(TEX.px, {
      lifespan: { min: 1400, max: 2200 }, speedY: { min: -14, max: -5 }, speedX: { min: -8, max: 8 }, scale: { start: 0.6, end: 0.2 }, alpha: { start: 0.6, end: 0 },
      tint: SILK, blendMode: ADD, frequency: 420,
      emitZone: this.zone(-side * 0.5, -side * 0.95, side, side * 0.7),
    });
  }

  /** A basilisk: swamp mist, marsh-lights, venom dripping off its jaws, its yellow eye. */
  private buildSerpent(): void {
    const side = this.side;
    this.emit(TEX.smoke, {
      lifespan: { min: 2000, max: 3200 }, speedX: { min: 4, max: 14 }, speedY: { min: -3, max: 1 }, scale: { start: 0.6, end: 1.7 }, alpha: { start: 0.3, end: 0 },
      tint: [0x7a8f6a, 0x5f7350, 0x9aa88a], blendMode: NORMAL, frequency: 230,
      emitZone: this.zone(-side * 1.0, -side * 0.3, side * 1.6, side * 0.3),
    });
    // marsh-lights: little green wills wandering round it
    this.emit(TEX.px, {
      lifespan: { min: 1600, max: 2600 }, speedX: { min: -10, max: 10 }, speedY: { min: -8, max: 4 }, scale: { start: 1, end: 0.4 }, alpha: { start: 0.9, end: 0, ease: 'Sine.easeIn' },
      color: [0xe8ff9a, 0xa8f05a, 0x5ac02a], blendMode: ADD, frequency: 600,
      emitZone: this.zone(-side * 1.1, -side * 1.2, side * 2.2, side * 1.1),
    });
    // venom dripping off its jaws
    this.emitAt({ x: -0.34, y: -0.55 }, TEX.drop, {
      lifespan: { min: 380, max: 560 }, speedY: { min: 0, max: 10 }, gravityY: 260, scale: { start: 0.8, end: 0.6 }, alpha: { start: 1, end: 0.2 },
      tint: VENOM, blendMode: NORMAL, frequency: 520,
    });
    // its eye: a sickly yellow light that never quite goes out
    const eye = EYE.serpent!;
    this.spark = this.light(0, 0, side * 0.32, side * 0.32, 0xf0e040, 0.4, false);
    this.anchored.push({ obj: this.spark, at: eye });
  }

  /** A black dragon: darkness pooled round it, black smoke curling up, dull embers, falling ash, a red eye. */
  private buildWyrm(): void {
    const side = this.side;
    const { scene } = this.host;
    this.shade = scene.add.image(0, -side * 0.45, TEX.glow).setTint(0x000000).setBlendMode(NORMAL).setAlpha(0.5);
    this.shade.setDisplaySize(side * 2.4, side * 1.7);
    this.under(this.shade);
    this.parts.push(this.shade);
    // a dull, sullen red behind it, like a coal under ash
    this.glow = this.light(0, -side * 0.5, side * 1.3, side * 1.1, 0x8a1c0c, 0.18, true);
    this.glowBase = 0.1;
    this.emit(TEX.smoke, {
      lifespan: { min: 1600, max: 2600 }, speedY: { min: -22, max: -8 }, speedX: { min: -10, max: 10 }, rotate: { min: 0, max: 360 }, scale: { start: 0.5, end: 1.8 }, alpha: { start: 0.55, end: 0 },
      tint: [0x141016, 0x221a22, 0x0a080a], blendMode: NORMAL, frequency: 150,
      emitZone: this.zone(-side * 0.45, -side * 0.9, side * 0.9, side * 0.6),
    });
    this.emit(TEX.px, {
      lifespan: { min: 900, max: 1600 }, speedY: { min: -30, max: -10 }, speedX: { min: -10, max: 10 }, scale: { start: 0.8, end: 0 }, color: [0xd04a1a, 0x8a1c0c, 0x3a0806], blendMode: ADD, frequency: 210,
      emitZone: this.zone(-side * 0.4, -side * 0.8, side * 0.8, side * 0.6),
    });
    // ash, sifting down round it
    this.emit(TEX.px, {
      lifespan: { min: 2000, max: 3200 }, speedY: { min: 6, max: 16 }, speedX: { min: -8, max: 8 }, scale: { min: 0.5, max: 0.9 }, alpha: { start: 0.9, end: 0 },
      tint: [0x6a6468, 0x4a4448, 0x8a8488], blendMode: NORMAL, frequency: 110,
      emitZone: this.zone(-side * 1.1, -side * 1.6, side * 2.2, side * 0.8),
    });
    const eye = EYE.wyrm!;
    this.spark = this.light(0, 0, side * 0.26, side * 0.26, 0xff2a2a, 0.45, false);
    this.anchored.push({ obj: this.spark, at: eye });
  }

  /** A great eagle (docs/95): it hovers on its wings, the wind streaming past it and feathers drifting off. */
  private buildEagle(): void {
    const side = this.side;
    // wind: thin pale streaks blowing across it, low and high
    this.emit(TEX.px, {
      lifespan: { min: 500, max: 800 }, speedX: { min: -150, max: -90 }, speedY: { min: -4, max: 4 }, scaleX: { start: 6, end: 2 }, scaleY: 0.5, alpha: { start: 0.45, end: 0 },
      tint: [0xffffff, 0xe8f0f4], blendMode: NORMAL, frequency: 140,
      emitZone: this.zone(-side * 0.4, -side * 1.4, side * 1.6, side * 1.3),
    });
    // feathers shed in the beat of its wings, drifting down and away
    this.emit(TEX.leaf, {
      lifespan: { min: 1800, max: 2800 }, speedY: { min: 6, max: 16 }, speedX: { min: -22, max: 6 }, rotate: { start: 0, end: 360 }, scale: { min: 0.7, max: 1.1 }, alpha: { start: 1, end: 0, ease: 'Sine.easeIn' },
      tint: FEATHERS, blendMode: NORMAL, frequency: 700,
      emitZone: this.zone(-side * 0.6, -side * 1.2, side * 1.2, side * 0.6),
    });
    // dust lifted off the ground under its wings
    this.emit(TEX.smoke, {
      lifespan: { min: 900, max: 1500 }, speedX: { min: -30, max: 30 }, speedY: { min: -6, max: -1 }, scale: { start: 0.3, end: 0.9 }, alpha: { start: 0.25, end: 0 },
      tint: this.look.dust, blendMode: NORMAL, frequency: 380,
      emitZone: this.zone(-side * 0.6, -4, side * 1.2, 4),
    });
    // its eye: a fierce gold glint
    this.spark = this.light(0, 0, side * 0.18, side * 0.18, 0xffd040, 0.3, false);
    this.anchored.push({ obj: this.spark, at: EYE.eagle! });
  }

  /** An ent (docs/95): leaves falling off its crown, fireflies and pollen round it, its eyes a deep green glow. */
  private buildEnt(): void {
    const side = this.side;
    this.glow = this.light(0, -side * 0.5, side * 1.6, side * 1.4, 0x6ac83a, 0.12, true);
    this.glowBase = 0.08;
    this.emit(TEX.leaf, {
      lifespan: { min: 2200, max: 3400 }, speedY: { min: 8, max: 18 }, speedX: { min: -10, max: 10 }, rotate: { start: 0, end: 540 }, scale: { min: 0.7, max: 1.1 }, alpha: { start: 1, end: 0, ease: 'Sine.easeIn' },
      tint: LEAVES, blendMode: NORMAL, frequency: 260,
      emitZone: this.zone(-side * 0.55, -side * 1.3, side * 1.1, side * 0.5),
    });
    // fireflies: slow, wandering, winking out
    this.emit(TEX.px, {
      lifespan: { min: 1800, max: 3000 }, speedX: { min: -8, max: 8 }, speedY: { min: -8, max: 4 }, scale: { start: 0.9, end: 0.4 }, alpha: { start: 0.95, end: 0, ease: 'Sine.easeIn' },
      color: [0xf8ff9a, 0xd8f05a, 0x9ad83a], blendMode: ADD, frequency: 420,
      emitZone: this.zone(-side * 1.0, -side * 1.3, side * 2.0, side * 1.2),
    });
    // pollen: a faint golden haze drifting up
    this.emit(TEX.px, {
      lifespan: { min: 2000, max: 3000 }, speedY: { min: -10, max: -3 }, speedX: { min: -5, max: 5 }, scale: { start: 0.5, end: 0.2 }, alpha: { start: 0.5, end: 0 },
      tint: [0xf0e080, 0xe0d070], blendMode: ADD, frequency: 300,
      emitZone: this.zone(-side * 0.6, -side * 0.6, side * 1.2, side * 0.6),
    });
    this.spark = this.light(0, 0, side * 0.32, side * 0.2, 0x8aff4a, 0.5, false);
    this.anchored.push({ obj: this.spark, at: EYE.ent! });
  }

  /** A grim giant (docs/95): snow falling on it, cold breath steaming from its beard, runes glinting on its belt. */
  private buildGiant(): void {
    const side = this.side;
    this.glow = this.light(0, -side * 0.5, side * 1.6, side * 1.4, 0x8ad0ff, 0.14, true);
    this.glowBase = 0.1;
    this.emit(TEX.flake, {
      lifespan: { min: 2000, max: 3000 }, speedY: { min: 8, max: 18 }, speedX: { min: -10, max: 4 }, rotate: { min: 0, max: 360 }, scale: { min: 0.35, max: 0.75 }, alpha: { start: 0.9, end: 0 },
      tint: [0xffffff, 0xeaf6ff, 0xd0e6f8], blendMode: NORMAL, frequency: 170,
      emitZone: this.zone(-side * 1.0, -side * 1.7, side * 2.0, side * 1.0),
    });
    // cold breath: a plume of steam from its beard at the top of each breath
    this.emitAt(this.look.head, TEX.smoke, {
      lifespan: { min: 800, max: 1300 }, speedX: { min: -10, max: 10 }, speedY: { min: -14, max: -5 }, scale: { start: 0.25, end: 1.0 }, alpha: { start: 0.5, end: 0 },
      tint: [0xf4faff, 0xdcecf8], blendMode: NORMAL, frequency: this.look.breath * 2 * Math.PI, quantity: 6,
    });
    // rune glints on its belt and its club
    this.emitAt(BELT, TEX.px, {
      lifespan: { min: 280, max: 520 }, scale: { start: 1, end: 0 }, alpha: { start: 1, end: 0 }, color: [0xffffff, RUNE], blendMode: ADD, frequency: 380,
      emitZone: this.zone(-side * 0.18, -side * 0.04, side * 0.36, side * 0.08),
    });
    this.emitAt(CLUB, TEX.px, {
      lifespan: { min: 300, max: 600 }, scale: { start: 0.9, end: 0 }, alpha: { start: 1, end: 0 }, color: [0xffffff, RUNE], blendMode: ADD, frequency: 640,
      emitZone: this.zone(-side * 0.08, -side * 0.08, side * 0.16, side * 0.16),
    });
    this.spark = this.light(0, 0, side * 0.4, side * 0.2, RUNE, 0.25, false);
    this.anchored.push({ obj: this.spark, at: BELT });
  }

  /**
   * The kraken (docs/116): foam churning in a ring round its footprint, bubbles rising, ink
   * swirling under it, its two red eyes glowing and, now and then, an arm tip breaking the water.
   */
  private buildKraken(): void {
    const side = this.side;
    const { scene } = this.host;
    // the ink pooled round it, and a cold deep-water light behind
    this.shade = scene.add.image(0, -side * 0.45, TEX.glow).setTint(0x04060c).setBlendMode(NORMAL).setAlpha(0.45);
    this.shade.setDisplaySize(side * 2.2, side * 1.5);
    this.under(this.shade);
    this.parts.push(this.shade);
    this.glow = this.light(0, -side * 0.5, side * 1.7, side * 1.4, 0x2a7a9a, 0.14, true);
    this.glowBase = 0.1;
    // the ring of foam round its footprint (it swells and settles with the swell, `update`)
    this.foam = scene.add.image(0, -side * 0.42, TEX.ring).setTint(0xe8f8fa).setBlendMode(ADD).setAlpha(0.35);
    this.foam.setDisplaySize(side * 1.3, side * 0.65);
    this.under(this.foam);
    this.parts.push(this.foam);
    // foam churning on the ring, white flecks thrown up and falling back
    this.emit(TEX.px, {
      lifespan: { min: 400, max: 800 }, speedY: { min: -18, max: -4 }, speedX: { min: -8, max: 8 }, gravityY: 50, scale: { min: 0.6, max: 1.1 }, alpha: { start: 0.9, end: 0 },
      tint: FOAM, blendMode: NORMAL, frequency: 60,
      emitZone: { type: 'edge', source: new Phaser.Geom.Ellipse(0, -side * 0.42, side * 1.25, side * 0.6) as unknown as Phaser.Types.GameObjects.Particles.EdgeZoneSource, quantity: 64 },
    });
    // bubbles rising out of the deep round it
    this.emit(TEX.dot, {
      lifespan: { min: 900, max: 1500 }, speedY: { min: -22, max: -8 }, speedX: { min: -4, max: 4 }, scale: { start: 0.5, end: 0.9 }, alpha: { start: 0.75, end: 0, ease: 'Expo.easeIn' },
      tint: [0xd8f4ff, 0xa8d8ec, 0x7ab8d0], blendMode: NORMAL, frequency: 150,
      emitZone: this.zone(-side * 0.7, -side * 0.6, side * 1.4, side * 0.5),
    });
    // ink seeping out low and drifting off
    this.emit(TEX.smoke, {
      lifespan: { min: 1800, max: 2800 }, speedX: { min: -10, max: 10 }, speedY: { min: -3, max: 1 }, rotate: { min: 0, max: 360 }, scale: { start: 0.5, end: 1.6 }, alpha: { start: 0.35, end: 0 },
      tint: INK, blendMode: NORMAL, frequency: 320,
      emitZone: this.zone(-side * 0.6, -side * 0.3, side * 1.2, side * 0.25),
    });
    // an arm tip breaks the water: brine streaming off it, a ripple where it went up
    this.every(900, () => {
      const tip = ARM_TIPS[Math.floor(Math.random() * ARM_TIPS.length)]!;
      const p = this.onArt(tip);
      this.burst(TEX.drop, p.x, p.y, 6, { speed: { min: 10, max: 40 }, angle: { min: 200, max: 340 }, gravityY: 260, lifespan: { min: 380, max: 620 }, scale: { start: 0.8, end: 0.5 }, tint: BRINE });
      this.ring(p.x, Math.min(p.y + side * 0.15, -side * 0.08), side * 0.7, 0xc8f0f4, 520);
    });
    // its two red eyes, glowing up through the murk
    this.spark = this.light(0, 0, side * 0.2, side * 0.2, 0xff2a2a, 0.45, false);
    this.anchored.push({ obj: this.spark, at: EYE.kraken! });
    this.twin = this.light(0, 0, side * 0.2, side * 0.2, 0xff2a2a, 0.45, false);
    this.anchored.push({ obj: this.twin, at: KRAKEN_EYE });
  }

  /**
   * The maw of the deep (docs/116): an abyssal violet glow, teeth glinting at its mouth, brine and
   * drool dripping off it, its many small orange eyes winking open, a ring of black water round it.
   */
  private buildMaw(): void {
    const side = this.side;
    const { scene } = this.host;
    this.shade = scene.add.image(0, -side * 0.45, TEX.glow).setTint(0x000000).setBlendMode(NORMAL).setAlpha(0.5);
    this.shade.setDisplaySize(side * 2.3, side * 1.6);
    this.under(this.shade);
    this.parts.push(this.shade);
    this.glow = this.light(0, -side * 0.5, side * 1.6, side * 1.3, ABYSS, 0.16, true);
    this.glowBase = 0.1;
    // teeth catching the light round its mouth
    this.emitAt(MAW_MOUTH, TEX.px, {
      lifespan: { min: 200, max: 420 }, scale: { start: 1.1, end: 0 }, alpha: { start: 1, end: 0 }, color: [0xffffff, 0xf0e4c8], blendMode: ADD, frequency: 260,
      emitZone: this.zone(-side * 0.14, -side * 0.12, side * 0.28, side * 0.24),
    });
    // drool falling off its jaws, and brine running off its hide
    this.emitAt({ x: MAW_MOUTH.x, y: MAW_MOUTH.y + 0.1 }, TEX.drop, {
      lifespan: { min: 380, max: 600 }, speedY: { min: 0, max: 10 }, gravityY: 240, scale: { start: 0.8, end: 0.6 }, alpha: { start: 1, end: 0.2 },
      tint: DROOL, blendMode: NORMAL, frequency: 300,
      emitZone: this.zone(-side * 0.12, 0, side * 0.24, side * 0.04),
    });
    this.emit(TEX.drop, {
      lifespan: { min: 420, max: 640 }, speedY: { min: 0, max: 8 }, gravityY: 240, scale: { start: 0.6, end: 0.5 }, alpha: { start: 0.9, end: 0.1 },
      tint: BRINE, blendMode: NORMAL, frequency: 360,
      emitZone: this.zone(-side * 0.35, -side * 0.85, side * 0.8, side * 0.5),
    });
    // its eyes: little orange lights winking open and shut all over its back
    this.emitAt(MAW_BACK, TEX.px, {
      lifespan: { min: 500, max: 1100 }, scale: { start: 0.9, end: 0.6 }, alpha: { start: 1, end: 0, ease: 'Expo.easeIn' }, color: EYES, blendMode: ADD, frequency: 180,
      emitZone: this.zone(-side * 0.2, -side * 0.1, side * 0.42, side * 0.22),
    });
    // a slow violet haze off the black water
    this.emit(TEX.smoke, {
      lifespan: { min: 1600, max: 2600 }, speedX: { min: -8, max: 8 }, speedY: { min: -6, max: -1 }, scale: { start: 0.4, end: 1.3 }, alpha: { start: 0.25, end: 0 },
      tint: [0x3a1a5a, 0x2a1240, 0x4a2a6a], blendMode: NORMAL, frequency: 340,
      emitZone: this.zone(-side * 0.7, -side * 0.25, side * 1.4, side * 0.2),
    });
    this.spark = this.light(0, 0, side * 0.16, side * 0.16, 0xff8a2a, 0.4, false);
    this.anchored.push({ obj: this.spark, at: EYE.maw! });
  }

  private update(t: number): void {
    if (this.dead) return;
    this.time = t;
    // what sits on the art follows it when it turns (even under reduce motion)
    for (const a of this.anchored) { const p = this.onArt(a.at); a.obj.x = p.x; a.obj.y = p.y + (a.dy ?? 0); }
    if (!this.q.still && !this.down) {
      // it breathes: a slow swell of the chest, its glow with it
      const breath = 0.5 + 0.5 * Math.sin(t / this.look.breath + this.host.unitId);
      for (const b of this.host.bodies()) b.setScale(b.scaleX, b.scaleX * (1 + 0.018 * breath));
      const flicker = 0.06 * Math.sin(t / 73) * Math.sin(t / 131);
      switch (this.kind) {
        case 'spider': {
          // a heartbeat: two quick throbs, then a rest
          const ph = (t / 1100 + this.host.unitId * 0.37) % 1;
          const beat = Math.max(0, Math.sin(ph * Math.PI * 7)) * (ph < 0.3 ? 1 : 0);
          this.setA(this.glow, this.glowBase + 0.5 * beat);
          this.setA(this.spark, 0.18 + 0.25 * beat);
          this.spark?.setRotation(t / 9000);
          break;
        }
        case 'serpent':
          this.setA(this.spark, 0.3 + 0.15 * Math.sin(t / 400) + flicker);
          break;
        case 'wyrm':
          this.setA(this.glow, this.glowBase + 0.12 * breath + flicker);
          this.setA(this.spark, 0.35 + 0.25 * Math.max(0, Math.sin(t / 1700 + 1)) + flicker);
          this.setA(this.shade, 0.45 + 0.1 * breath);
          break;
        case 'eagle': {
          // it hovers: a slow bob on its wingbeats (its shadow on the ground swells with it, hugeGround)
          const lift = 2.5 + 2.5 * Math.sin(t / 625);
          this.hover(lift);
          this.setA(this.spark, 0.2 + 0.15 * Math.max(0, Math.sin(t / 900 + 2)));
          break;
        }
        case 'ent':
          this.setA(this.glow, this.glowBase + 0.1 * breath);
          this.setA(this.spark, 0.4 + 0.2 * Math.sin(t / 1300) + flicker);
          break;
        case 'giant': {
          // the belt's runes wake as the ring under it runs round
          this.setA(this.glow, this.glowBase + 0.12 * breath + flicker);
          this.setA(this.spark, 0.15 + 0.3 * Math.pow(0.5 + 0.5 * Math.sin(t / 900), 3));
          break;
        }
        case 'kraken': {
          // a slow swell: the foam ring rolls out and back, its eyes brighten and dim under the water
          const swell = Math.sin(t / 1400 + this.host.unitId);
          this.setA(this.glow, this.glowBase + 0.08 * breath);
          const eyes = 0.3 + 0.25 * Math.max(0, Math.sin(t / 1900 + 0.5)) + flicker;
          this.setA(this.spark, eyes); this.setA(this.twin, eyes);
          this.setA(this.shade, 0.4 + 0.08 * breath);
          if (this.foam) { this.foam.setDisplaySize(this.side * (1.3 + 0.08 * swell), this.side * (0.65 + 0.04 * swell)); this.setA(this.foam, 0.28 + 0.1 * swell); }
          break;
        }
        case 'maw': {
          // the abyss under it throbs, its brightest eye twitching
          this.setA(this.glow, this.glowBase + 0.18 * Math.pow(0.5 + 0.5 * Math.sin(t / 700 + this.host.unitId), 2));
          this.setA(this.spark, 0.3 + 0.3 * Math.max(0, Math.sin(t / 530)) + flicker * 2);
          this.setA(this.shade, 0.45 + 0.08 * breath);
          break;
        }
        default:
          this.setA(this.glow, this.glowBase + 0.22 * breath + flicker);
      }
    }
    if (this.shader && !this.q.still && (this.lastRender < 0 || t - this.lastRender >= 50)) { this.lastRender = t; this.shader.renderImmediate(); }
  }

  // ---- moments ---------------------------------------------------------------------------------

  /** It sets down: each kicks up its own ground — rock and dust, ash, snow, sand, spiderlings, swamp water. */
  land(): void {
    if (this.dead) return;
    const side = this.side;
    const look = this.look;
    this.ring(0, -side * 0.1, side * 1.9, look.ring, 520);
    switch (this.kind) {
      case 'troll':
        this.crack(0, -side * 0.15, side * 0.9, 1600);
        this.burst(TEX.shard, 0, -side * 0.1, 16, { speed: { min: 50, max: 120 }, angle: { min: 200, max: 340 }, gravityY: 260, lifespan: { min: 500, max: 900 }, scale: { start: 1, end: 0.6 }, tint: look.shard, rotate: { min: 0, max: 360 } });
        playSfx('huge.debris', { space: hugeSpace(this.host.size) });
        break;
      case 'dragon':
        this.burst(TEX.flame, 0, -side * 0.2, 14, { speed: { min: 20, max: 60 }, angle: { min: 180, max: 360 }, lifespan: { min: 400, max: 800 }, scale: { start: 1, end: 0 }, color: [0xffd060, 0xf05a14, 0x6a1408], blendMode: ADD });
        break;
      case 'yeti':
        this.burst(TEX.flake, 0, -side * 0.1, 18, { speed: { min: 30, max: 80 }, angle: { min: 190, max: 350 }, gravityY: 60, lifespan: { min: 600, max: 1000 }, scale: { start: 1, end: 0.3 }, rotate: { min: 0, max: 360 }, tint: look.dust });
        this.puff(0, -side * 0.1, 10, look.dust, 0.5);
        break;
      case 'worm':
        this.spout(0, -side * 0.15, 1.2);
        playSfx('huge.debris', { space: hugeSpace(this.host.size) });
        break;
      case 'spider':
        this.burst(TEX.dot, 0, -side * 0.2, 16, { speed: { min: 40, max: 90 }, angle: { min: 0, max: 360 }, lifespan: { min: 500, max: 900 }, alpha: { start: 1, end: 0 }, tint: [0x140c12, 0x2a1420, 0x3a0c14] });
        break;
      case 'serpent':
        this.burst(TEX.drop, 0, -side * 0.1, 14, { speed: { min: 40, max: 90 }, angle: { min: 200, max: 340 }, gravityY: 300, lifespan: { min: 400, max: 700 }, tint: [0x4a5a34, 0x6a7a4a, 0x2a3a1a] });
        break;
      case 'wyrm':
        this.puff(0, -side * 0.1, 14, look.dust, 0.6);
        break;
      case 'eagle':
        // it settles on its wings: a gust of dust and loose feathers
        this.ring(0, -side * 0.1, side * 2.4, look.ring, 600);
        this.puff(0, -side * 0.05, 10, look.dust, 0.4, 1.6, true);
        this.burst(TEX.leaf, 0, -side * 0.5, 8, { speed: { min: 20, max: 60 }, angle: { min: 0, max: 360 }, gravityY: 30, lifespan: { min: 800, max: 1300 }, rotate: { min: 0, max: 360 }, tint: FEATHERS });
        break;
      case 'ent':
        this.burst(TEX.leaf, 0, -side * 0.8, 16, { speed: { min: 20, max: 60 }, angle: { min: 0, max: 360 }, gravityY: 40, lifespan: { min: 900, max: 1500 }, rotate: { min: 0, max: 360 }, tint: LEAVES });
        this.puff(0, -side * 0.1, 10, look.dust, 0.45, 1.4, true);
        playSfx('huge.debris', { space: hugeSpace(this.host.size) });
        break;
      case 'giant':
        this.crack(0, -side * 0.15, side * 0.8, 1500);
        this.burst(TEX.flake, 0, -side * 0.1, 16, { speed: { min: 30, max: 80 }, angle: { min: 190, max: 350 }, gravityY: 60, lifespan: { min: 600, max: 1000 }, rotate: { min: 0, max: 360 }, tint: look.dust });
        this.puff(0, -side * 0.1, 10, look.dust, 0.5);
        playSfx('huge.debris', { space: hugeSpace(this.host.size) });
        break;
      case 'kraken':
      case 'maw':
        // docs/116: it comes down in the water: a sheet of spray thrown up, foam rolling out
        this.splash(0, -side * 0.15, 1.4, this.kind === 'maw');
        this.ring(0, -side * 0.1, side * 2.4, this.kind === 'maw' ? 0x1a1024 : 0xe8f8fa, 640);
        playSfx('cue.splash', { space: hugeSpace(this.host.size) });
        break;
    }
  }

  /** A move (docs/92, docs/94): `at` is where it is aimed, in world pixels (a foe's chest), when it is aimed. */
  play(moment: HugeMoment, at?: Pt, ms = 900): void {
    if (this.dead) return;
    const side = this.side;
    const c = this.host.container;
    const local = at ? { x: at.x - c.x, y: at.y - c.y } : undefined;
    const look = this.look;
    const scene = this.host.scene;
    const space = hugeSpace(this.host.size);
    switch (moment) {
      case 'breath': {
        // a torrent from its jaws to the foe and past it, then the wash of flame where it lands
        // (docs/95: from its head on the art, which is on its left as drawn; not the far side)
        const face = this.headSide();
        const { from: mouth, to } = this.aim(local);
        const dx = to.x - mouth.x, dy = to.y - mouth.y;
        const ang = Phaser.Math.RadToDeg(Math.atan2(dy, dx));
        const dist = Math.max(side, Math.hypot(dx, dy));
        const flow = Math.max(300, ms * 0.6);
        const jet = this.emitter(TEX.flame, mouth.x, mouth.y, {
          lifespan: { min: 420, max: 620 }, speed: { min: dist * 1.6, max: dist * 2.3 }, angle: { min: ang - 16, max: ang + 16 },
          scale: { start: 0.9, end: 2.2 }, alpha: { start: 1, end: 0 }, color: [0xffffff, 0xfff1a0, 0xffb02e, 0xf05a14, 0x6a1408], colorEase: 'Quad.easeIn', blendMode: ADD, frequency: 12, quantity: 3,
        });
        const sparks = this.emitter(TEX.px, mouth.x, mouth.y, {
          lifespan: { min: 300, max: 520 }, speed: { min: dist * 1.8, max: dist * 2.6 }, angle: { min: ang - 22, max: ang + 22 }, scale: { start: 1, end: 0 }, color: [0xffffff, 0xffd060], blendMode: ADD, frequency: 30,
        });
        scene.time.delayedCall(flow, () => { jet?.stop(); sparks?.stop(); });
        scene.time.delayedCall(flow * 0.55, () => {
          this.flash(to.x, to.y, side * 1.4, 0xff8a2a, 420);
          playSfx('huge.fire_hit', { space });
        });
        scene.tweens.add({ targets: this.host.body, x: face * -2, duration: 120, yoyo: true, repeat: 2 });
        break;
      }
      case 'wings': {
        // great wings beat: it lifts, two gusts roll out (dust, or the black dragon's ash and smoke), and it comes down hard
        const wyrm = this.kind === 'wyrm';
        const gust = wyrm ? 0x3a3034 : 0xb59a7a;
        scene.tweens.add({ targets: this.host.body, y: wyrm ? -14 : -8, duration: Math.max(120, ms * 0.25), yoyo: true, repeat: 1, ease: 'Sine.easeOut' });
        this.ring(0, -side * 0.1, side * 2.4, gust, 600);
        scene.time.delayedCall(260, () => this.ring(0, -side * 0.1, side * 2.8, gust, 640));
        this.burst(TEX.smoke, 0, -4, 14, { speed: { min: 60, max: 110 }, angle: { min: 0, max: 360 }, lifespan: { min: 500, max: 900 }, scale: { start: 0.4, end: 1.1 }, alpha: { start: wyrm ? 0.7 : 0.5, end: 0 }, tint: wyrm ? look.dust : [0x9a8a72, 0x7a6c58] });
        if (this.kind === 'eagle') this.burst(TEX.leaf, 0, -side * 0.6, 14, { speed: { min: 40, max: 110 }, angle: { min: 0, max: 360 }, gravityY: 30, lifespan: { min: 800, max: 1400 }, rotate: { min: 0, max: 360 }, tint: FEATHERS });
        if (wyrm) {
          // it drops on the foe out of the smoke: a rend of dark red there
          if (local) scene.time.delayedCall(Math.max(160, ms * 0.4), () => this.slash(local, 0xb02a1a));
          this.burst(TEX.px, 0, -side * 0.5, 16, { speed: { min: 30, max: 80 }, angle: { min: 0, max: 360 }, lifespan: { min: 600, max: 1100 }, scale: { start: 0.9, end: 0 }, color: [0xd04a1a, 0x8a1c0c], blendMode: ADD });
        }
        this.shake(260, 0.003);
        break;
      }
      case 'roar': {
        // a shock ring out of its chest, the air shaking — and what it shakes loose
        const colour = this.kind === 'dragon' ? 0xff7a2a : this.kind === 'troll' ? 0xd8c8a8 : look.ring;
        this.ring(0, -side * 0.6, side * 3, colour, 700);
        scene.time.delayedCall(140, () => this.ring(0, -side * 0.6, side * 2.2, this.kind === 'wyrm' ? 0x8a1c0c : 0xffffff, 520));
        if (this.kind === 'yeti') {
          // the snow on it is shaken off in a blast, and the air goes white for a breath
          this.burst(TEX.flake, 0, -side * 0.7, 26, { speed: { min: 50, max: 140 }, angle: { min: 0, max: 360 }, lifespan: { min: 600, max: 1100 }, scale: { start: 1.1, end: 0.2 }, rotate: { min: 0, max: 360 }, tint: look.dust });
          this.flash(0, -side * 0.6, side * 2.6, 0xcfeeff, 520);
        } else if (this.kind === 'worm') {
          this.spout(-side * 0.5, -side * 0.2, 0.8);
          this.spout(side * 0.5, -side * 0.3, 0.8);
        } else if (this.kind === 'serpent') {
          // a dry rattle: its scales bristle, venom flicked off them
          scene.tweens.add({ targets: this.host.bodies(), x: '+=1', duration: 40, yoyo: true, repeat: 7 });
          this.burst(TEX.drop, 0, -side * 0.6, 12, { speed: { min: 40, max: 90 }, angle: { min: 200, max: 340 }, gravityY: 260, lifespan: { min: 400, max: 700 }, tint: VENOM });
        } else if (this.kind === 'spider') {
          this.flare(this.glow, 0.95, 500);
        } else if (this.kind === 'wyrm') {
          this.flare(this.spark, 1, 600);
          this.puff(0, -side * 0.6, 16, look.dust, 0.65);
        } else if (this.kind === 'giant') {
          // its breath smokes in the cold and the runes on it flare
          const head = this.onArt(this.look.head);
          this.puff(head.x, head.y, 12, look.dust, 0.55, 1.8);
          this.flare(this.spark, 1, 700);
          this.flare(this.glow, 0.5, 700);
          this.burst(TEX.flake, 0, -side * 0.7, 18, { speed: { min: 40, max: 120 }, angle: { min: 0, max: 360 }, lifespan: { min: 600, max: 1100 }, rotate: { min: 0, max: 360 }, tint: look.dust });
        } else if (this.kind === 'ent') {
          this.flare(this.spark, 1, 800);
          this.burst(TEX.leaf, 0, -side * 0.9, 20, { speed: { min: 30, max: 90 }, angle: { min: 0, max: 360 }, gravityY: 40, lifespan: { min: 900, max: 1500 }, rotate: { min: 0, max: 360 }, tint: LEAVES });
        } else if (this.kind === 'eagle') {
          this.screech(local, ms);
        } else if (this.kind === 'kraken') {
          // docs/116: its eyes blaze, and every arm heaves up out of the water at once
          this.flare(this.spark, 1, 700);
          this.flare(this.twin, 1, 700);
          ARM_TIPS.forEach((tip, i) => scene.time.delayedCall(i * 60, () => { const p = this.onArt(tip); this.splash(p.x, p.y, 0.5); }));
        } else if (this.kind === 'maw') {
          // its mouth gapes: the violet flares, its eyes all open, drool flung off its teeth
          const mouth = this.onArt(MAW_MOUTH);
          this.flare(this.glow, 0.6, 700);
          this.flare(this.spark, 1, 700);
          this.flash(mouth.x, mouth.y, side * 1.4, ABYSS, 520);
          this.burst(TEX.drop, mouth.x, mouth.y, 12, { speed: { min: 40, max: 100 }, angle: { min: 120, max: 240 }, gravityY: 240, lifespan: { min: 400, max: 700 }, tint: DROOL });
          this.manyEyes(16, ms);
        }
        this.shake(380, this.kind === 'dragon' || this.kind === 'wyrm' || this.kind === 'worm' ? 0.005 : 0.004);
        break;
      }
      case 'smash': {
        // the blow comes down: the ground splits under it and shards jump (rock, ice, sand, splinters)
        const hit = local ?? { x: this.headSide() * side * 0.8, y: -side * 0.2 };
        if (this.kind !== 'troll') this.lunge(hit, 6, ms);
        scene.time.delayedCall(ms * 0.35, () => {
          this.crack(hit.x, hit.y, side * 0.8, 1400);
          this.burst(TEX.shard, hit.x, hit.y, 12, { speed: { min: 40, max: 100 }, angle: { min: 200, max: 340 }, gravityY: 240, lifespan: { min: 450, max: 800 }, scale: { start: 1, end: 0.5 }, tint: look.shard, rotate: { min: 0, max: 360 } });
          this.ring(hit.x, hit.y, side * 1.4, this.kind === 'troll' ? 0x8a7a62 : look.ring, 420);
          if (this.kind === 'yeti') { this.flash(hit.x, hit.y, side * 1.2, 0xbfe6ff, 380); this.puff(hit.x, hit.y, 8, look.dust, 0.5); }
          if (this.kind === 'worm') this.spout(hit.x, hit.y, 0.8);
          if (this.kind === 'ent') this.burst(TEX.leaf, hit.x, hit.y - side * 0.3, 10, { speed: { min: 30, max: 80 }, angle: { min: 0, max: 360 }, gravityY: 60, lifespan: { min: 600, max: 1000 }, rotate: { min: 0, max: 360 }, tint: LEAVES });
          if (this.kind === 'giant') { this.flash(hit.x, hit.y, side * 1.2, RUNE, 380); this.puff(hit.x, hit.y, 8, look.dust, 0.5); this.glint(this.onArt(CLUB), RUNE, ms * 0.6); }
          this.shake(220, 0.004);
          playSfx('huge.debris', { space });
        });
        break;
      }
      case 'avalanche': {
        // rock (or the yeti's ice) comes down out of the sky on the foe, and the dust of it rolls out
        const hit = local ?? { x: side, y: -side * 0.3 };
        const ice = this.kind === 'yeti';
        const rocks = this.emitter(TEX.shard, hit.x, hit.y - side * 3, {
          lifespan: { min: 380, max: 520 }, speedY: { min: side * 4, max: side * 6 }, speedX: { min: -side * 0.8, max: side * 0.8 }, scale: { start: 1.6, end: 1.2 }, rotate: { min: 0, max: 360 },
          tint: ice ? look.shard : [0x8a8070, 0x6a6258, 0xa89c88], frequency: 28, quantity: 2,
        });
        scene.time.delayedCall(Math.max(260, ms * 0.45), () => rocks?.stop());
        scene.time.delayedCall(Math.max(200, ms * 0.35), () => {
          this.ring(hit.x, hit.y, side * 2.2, ice ? 0xdff2ff : 0x9a8a72, 700);
          this.puff(hit.x, hit.y, 16, ice ? look.dust : [0x9a8a72, 0x7a6c58], 0.55);
          if (ice) { this.flash(hit.x, hit.y, side * 1.6, 0xbfe6ff, 420); playSfx('spell.frost_hit', { space }); }
          this.shake(320, 0.005);
          playSfx('huge.debris', { space });
        });
        break;
      }

      // ---- the yeti ----
      case 'frost': {
        // a howling cone of snow and ice from its maw, the foe whited out, rime ringing out where it lands
        const { from, to, flow } = this.cone(local, ms, {
          tex: TEX.flake, spread: 22, scale: { start: 0.7, end: 1.6 }, rotate: { min: 0, max: 360 }, tint: [0xffffff, 0xeaf6ff, 0xbfe0f8, 0x8ac4ec],
        });
        const mist = this.emitter(TEX.smoke, from.x, from.y, {
          lifespan: { min: 420, max: 640 }, speed: { min: this.dist(from, to) * 1.2, max: this.dist(from, to) * 1.8 }, angle: this.spread(from, to, 18),
          scale: { start: 0.4, end: 2.2 }, alpha: { start: 0.6, end: 0 }, tint: [0xf4faff, 0xd8ecfa], blendMode: NORMAL, frequency: 24, quantity: 2,
        });
        scene.time.delayedCall(flow, () => mist?.stop());
        scene.time.delayedCall(flow * 0.55, () => {
          this.flash(to.x, to.y, side * 1.6, 0xcfeeff, 520);
          this.ring(to.x, to.y, side * 1.6, 0xdff2ff, 600);
          this.burst(TEX.shard, to.x, to.y, 10, { speed: { min: 30, max: 80 }, angle: { min: 0, max: 360 }, lifespan: { min: 400, max: 700 }, scale: { start: 1, end: 0.3 }, rotate: { min: 0, max: 360 }, tint: look.shard, blendMode: ADD });
          playSfx('spell.frost_hit', { space });
        });
        this.shake(Math.max(300, flow), 0.002);
        break;
      }
      case 'pounce': {
        // it bounds: a crouch, a great leap toward the foe, and it comes down in a burst of snow
        const to = local ?? { x: this.headSide() * side, y: -side * 0.2 };
        const dir = Math.sign(to.x) || this.headSide();
        const up = Math.max(140, ms * 0.3);
        const bodies = this.host.bodies();
        scene.tweens.chain({
          targets: bodies,
          tweens: [
            { y: '+=3', duration: 90, ease: 'Quad.easeIn' },
            { y: '-=21', x: `+=${dir * side * 0.3}`, duration: up, ease: 'Quad.easeOut' },
            { y: '+=18', x: `-=${dir * side * 0.3}`, duration: up * 0.8, ease: 'Quad.easeIn' },
          ],
        });
        this.burst(TEX.flake, 0, -2, 10, { speed: { min: 20, max: 60 }, angle: { min: 200, max: 340 }, lifespan: { min: 400, max: 700 }, tint: look.dust, rotate: { min: 0, max: 360 } });
        scene.time.delayedCall(90 + up * 1.8, () => {
          this.ring(to.x, to.y, side * 1.8, 0xdff2ff, 520);
          this.burst(TEX.flake, to.x, to.y, 22, { speed: { min: 40, max: 110 }, angle: { min: 180, max: 360 }, gravityY: 80, lifespan: { min: 500, max: 900 }, scale: { start: 1, end: 0.3 }, rotate: { min: 0, max: 360 }, tint: look.dust });
          this.puff(to.x, to.y, 10, look.dust, 0.55);
          this.slash(to, 0xe8f6ff);
          this.shake(240, 0.004);
          playSfx('huge.debris', { space });
        });
        break;
      }
      case 'rime': {
        // frost closes over its hide: crystals drawn in to it from the air, a cold flare, glitter
        this.gather(0, -side * 0.5, side * 1.1, TEX.flake, look.dust, ms);
        scene.time.delayedCall(Math.max(200, ms * 0.5), () => {
          this.flash(0, -side * 0.5, side * 1.8, 0x9fd8ff, 600);
          this.burst(TEX.px, 0, -side * 0.5, 20, { speed: { min: 10, max: 50 }, angle: { min: 0, max: 360 }, lifespan: { min: 500, max: 900 }, scale: { start: 1.2, end: 0 }, color: [0xffffff, 0xcff0ff], blendMode: ADD });
          playSfx('spell.frost_cast', { space });
        });
        break;
      }

      // ---- the sandworm ----
      case 'engulf': {
        // the sand opens under the foe: a dark pit, the sand pouring into it, then a gulp
        const to = local ?? { x: this.headSide() * side * 1.2, y: -side * 0.2 };
        const pit = this.shadow(to.x, to.y + side * 0.1, side * 1.6, side * 0.8, 0.85, ms * 1.6);
        this.gather(to.x, to.y + side * 0.1, side * 1.2, TEX.px, look.dust, ms, true);
        this.lunge(to, 10, ms);
        scene.time.delayedCall(Math.max(260, ms * 0.6), () => {
          this.ringIn(to.x, to.y + side * 0.1, side * 2.2, look.ring, 360);
          this.spout(to.x, to.y, 1);
          this.shake(360, 0.006);
          playSfx('huge.debris', { space });
          if (pit) scene.tweens.add({ targets: pit, scaleX: pit.scaleX * 0.3, scaleY: pit.scaleY * 0.3, duration: 360, ease: 'Back.easeIn' });
        });
        break;
      }
      case 'erupt': {
        // it dives — and the ground under the foe bursts upward, a geyser of sand and the maw in it
        const to = local ?? { x: this.headSide() * side, y: -side * 0.2 };
        scene.tweens.add({ targets: this.host.bodies(), y: '+=10', alpha: 0.55, duration: Math.max(120, ms * 0.25), yoyo: true, ease: 'Quad.easeIn' });
        this.ring(0, -side * 0.1, side * 1.6, look.ring, 400);
        // the sand rumbling toward the foe
        const trail = 5;
        for (let i = 1; i <= trail; i++) {
          const k = i / (trail + 1);
          scene.time.delayedCall(i * Math.max(30, ms * 0.06), () => this.puff(to.x * k, -side * 0.1 + (to.y + side * 0.1) * k, 3, look.dust, 0.4));
        }
        scene.time.delayedCall(Math.max(240, ms * 0.42), () => {
          this.crack(to.x, to.y, side * 0.9, 1500);
          this.spout(to.x, to.y, 2);
          this.burst(TEX.shard, to.x, to.y, 14, { speed: { min: 70, max: 150 }, angle: { min: 220, max: 320 }, gravityY: 320, lifespan: { min: 500, max: 900 }, scale: { start: 1.2, end: 0.6 }, rotate: { min: 0, max: 360 }, tint: look.shard });
          this.ring(to.x, to.y, side * 2.2, look.ring, 600);
          this.shake(380, 0.006);
          playSfx('spell.earth_burst', { space });
        });
        break;
      }
      case 'quake': {
        // the dunes heave: rings of sand rolling out one after another, plumes going up all round
        for (let i = 0; i < 3; i++) scene.time.delayedCall(i * 220, () => this.ring(0, -side * 0.1, side * (2.2 + i * 0.7), look.ring, 700));
        for (let i = 0; i < 5; i++) {
          scene.time.delayedCall(120 + i * 110, () => {
            const a = (i / 5) * Math.PI * 2 + 0.4, d = side * (0.8 + (i % 2) * 0.4);
            this.spout(Math.cos(a) * d, -side * 0.2 + Math.sin(a) * d * 0.4, 0.9);
          });
        }
        this.shake(620, 0.006);
        playSfx('huge.debris', { space });
        break;
      }
      case 'sandblast': {
        // a blast of grit out of its maw, scouring the foe
        const { to, flow } = this.cone(local, ms, { tex: TEX.px, spread: 18, scale: { start: 1.2, end: 0.6 }, tint: look.dust, blend: NORMAL });
        const { from } = this.aim(local);
        const haze = this.emitter(TEX.smoke, from.x, from.y, {
          lifespan: { min: 420, max: 640 }, speed: { min: this.dist(from, to) * 1.2, max: this.dist(from, to) * 1.7 }, angle: this.spread(from, to, 16),
          scale: { start: 0.4, end: 1.8 }, alpha: { start: 0.55, end: 0 }, tint: [0xd8bc88, 0xc8a874], blendMode: NORMAL, frequency: 26, quantity: 2,
        });
        scene.time.delayedCall(flow, () => haze?.stop());
        scene.time.delayedCall(flow * 0.55, () => { this.puff(to.x, to.y, 10, look.dust, 0.6); this.ring(to.x, to.y, side * 1.4, look.ring, 420); });
        break;
      }

      // ---- the spider queen ----
      case 'web': {
        // a line of silk shot from her to the foe, a net bursting open over it
        const { from, to } = this.aim(local, { x: -0.05, y: -0.4 });
        this.silkLine(from, to, Math.max(120, ms * 0.18), ms * 1.6);
        scene.time.delayedCall(Math.max(120, ms * 0.18), () => {
          this.net(to, side * 0.75, ms * 2);
          this.burst(TEX.px, to.x, to.y, 16, { speed: { min: 20, max: 70 }, angle: { min: 0, max: 360 }, lifespan: { min: 400, max: 700 }, scale: { start: 1, end: 0 }, tint: SILK, blendMode: ADD });
          playSfx('spell.web', { space });
        });
        break;
      }
      case 'brood': {
        // her back flares, the runes under her blaze, and the brood boils out of her in every direction
        this.flare(this.glow, 1, 700);
        this.flare(this.spark, 0.9, 900);
        this.ring(0, -side * 0.4, side * 2.6, 0xc01830, 700);
        const abd = this.onArt(ABDOMEN);
        this.burst(TEX.px, abd.x, abd.y, 14, { speed: { min: 30, max: 80 }, angle: { min: 180, max: 360 }, gravityY: 200, lifespan: { min: 400, max: 700 }, tint: [0xe8e0c8, 0xd0c8a8, 0xc0e070] });
        for (let i = 0; i < 3; i++) {
          scene.time.delayedCall(i * 160, () => this.burst(TEX.dot, 0, -side * 0.25, 22, {
            speed: { min: 40, max: 120 }, angle: { min: 0, max: 360 }, lifespan: { min: 700, max: 1300 }, scale: { min: 0.8, max: 1.2 }, alpha: { start: 1, end: 0, ease: 'Expo.easeIn' }, tint: [0x140c12, 0x2a1420, 0x3a0c14, 0x5a1020],
          }));
        }
        this.shake(300, 0.003);
        playSfx('spell.summon', { space });
        break;
      }
      case 'venom': {
        // venom rains on the foe out of the dark over it, and a sickly pool spreads there
        const to = local ?? { x: this.headSide() * side, y: -side * 0.3 };
        const rain = this.emitter(TEX.drop, to.x, to.y - side * 2.4, {
          lifespan: { min: 340, max: 460 }, speedY: { min: side * 4.5, max: side * 6 }, speedX: { min: -side * 0.2, max: side * 0.2 }, scale: { start: 1.1, end: 1 },
          tint: VENOM, frequency: 22, quantity: 2, emitZone: this.zone(-side * 0.6, 0, side * 1.2, 4),
        });
        scene.time.delayedCall(Math.max(280, ms * 0.5), () => rain?.stop());
        const mouth = this.onArt(this.look.head);
        this.burst(TEX.drop, mouth.x, mouth.y, 8, { speed: { min: 30, max: 70 }, angle: { min: 230, max: 310 }, gravityY: 260, lifespan: { min: 300, max: 600 }, tint: VENOM });
        scene.time.delayedCall(Math.max(200, ms * 0.35), () => {
          this.pool(to.x, to.y + side * 0.15, side * 1.4, 0x5fc824, ms * 1.8);
          this.flash(to.x, to.y, side * 1.2, 0x9cff4a, 420);
          this.burst(TEX.smoke, to.x, to.y, 8, { speed: { min: 10, max: 30 }, angle: { min: 220, max: 320 }, lifespan: { min: 700, max: 1100 }, scale: { start: 0.4, end: 1.2 }, alpha: { start: 0.45, end: 0 }, tint: [0x8ac84a, 0x5a8a2a] });
          playSfx('spell.poison', { space });
        });
        break;
      }
      case 'bite': {
        // a lunge and a bite: fangs flashing across the foe, and what drips off them
        const to = local ?? { x: this.headSide() * side, y: -side * 0.4 };
        this.lunge(to, this.kind === 'worm' ? 12 : 8, ms);
        const colour = this.kind === 'wyrm' ? 0xb02a1a : this.kind === 'worm' ? 0xe8d8b8 : this.kind === 'eagle' ? 0xfff0c0 : this.kind === 'maw' ? 0xf0e4c8 : 0xd8ff9a;
        scene.time.delayedCall(Math.max(120, ms * 0.25), () => {
          this.slash(to, colour);
          if (this.kind === 'spider' || this.kind === 'serpent') this.burst(TEX.drop, to.x, to.y, 10, { speed: { min: 30, max: 70 }, angle: { min: 200, max: 340 }, gravityY: 260, lifespan: { min: 300, max: 600 }, tint: VENOM });
          else if (this.kind === 'worm') this.puff(to.x, to.y, 6, look.dust, 0.5);
          else if (this.kind === 'maw' || this.kind === 'kraken') this.burst(TEX.drop, to.x, to.y, 12, { speed: { min: 30, max: 80 }, angle: { min: 200, max: 340 }, gravityY: 260, lifespan: { min: 300, max: 600 }, tint: this.kind === 'maw' ? DROOL : BRINE });
          else if (this.kind === 'eagle') this.burst(TEX.leaf, to.x, to.y, 8, { speed: { min: 30, max: 70 }, angle: { min: 0, max: 360 }, gravityY: 40, lifespan: { min: 600, max: 1000 }, rotate: { min: 0, max: 360 }, tint: FEATHERS });
          else this.burst(TEX.px, to.x, to.y, 10, { speed: { min: 30, max: 70 }, angle: { min: 0, max: 360 }, lifespan: { min: 400, max: 700 }, color: [0xd04a1a, 0x8a1c0c], blendMode: ADD });
        });
        break;
      }

      // ---- the basilisk ----
      case 'gaze': {
        // its eye glints gold — then a ring of stone-grey rolls out of it, and where it falls on the foe, stone
        const eye = this.onArt(EYE.serpent ?? this.look.head);
        const to = local ?? { x: this.headSide() * side * 1.5, y: -side * 0.4 };
        this.flare(this.spark, 1, 500);
        this.glint(eye, 0xfff27a, ms);
        scene.time.delayedCall(Math.max(160, ms * 0.3), () => {
          this.ring(eye.x, eye.y, side * 4, 0xa8a89c, 900);
          this.ring(eye.x, eye.y, side * 2.6, 0xf0e040, 600);
          this.sigil(to, 0xf0e040, ms * 1.2);
        });
        scene.time.delayedCall(Math.max(260, ms * 0.5), () => {
          this.flash(to.x, to.y, side * 1.4, 0xc8c8bc, 600);
          this.crack(to.x, to.y + side * 0.1, side * 0.6, 1400);
          this.burst(TEX.shard, to.x, to.y, 12, { speed: { min: 20, max: 70 }, angle: { min: 200, max: 340 }, gravityY: 200, lifespan: { min: 500, max: 900 }, scale: { start: 1, end: 0.6 }, rotate: { min: 0, max: 360 }, tint: look.shard });
          this.puff(to.x, to.y, 8, [0xa8a89c, 0x8a8a80], 0.5);
          playSfx('huge.debris', { space });
        });
        break;
      }
      case 'coil': {
        // it slides to the foe and its coils close on it: rings tightening, and a crushing squeeze
        // (docs/116: the kraken's coils are three of its arms, lashed out and wrapped round the foe in the water)
        const to = local ?? { x: this.headSide() * side, y: -side * 0.3 };
        const kraken = this.kind === 'kraken';
        this.lunge(to, 8, ms);
        if (kraken) for (let i = 0; i < 3; i++) scene.time.delayedCall(i * 90, () => this.tentacle(this.armToward(to, i), to, 0x3a2a3a, 0xc89a8a, Math.max(120, ms * 0.2), ms * 0.9, 4));
        for (let i = 0; i < 3; i++) scene.time.delayedCall(i * 140, () => this.ringIn(to.x, to.y + side * 0.1, side * (2 - i * 0.3), kraken ? (i % 2 ? 0x5a3a4a : 0xc8f0f4) : i % 2 ? 0x9a8a3a : 0x4a6a24, 420));
        scene.time.delayedCall(Math.max(300, ms * 0.55), () => {
          if (kraken) this.splash(to.x, to.y + side * 0.1, 0.8);
          else this.crack(to.x, to.y + side * 0.1, side * 0.5, 1100);
          this.burst(TEX.drop, to.x, to.y + side * 0.1, 10, { speed: { min: 30, max: 80 }, angle: { min: 200, max: 340 }, gravityY: 280, lifespan: { min: 400, max: 700 }, tint: kraken ? BRINE : [0x4a5a34, 0x6a7a4a] });
          this.shake(260, 0.004);
        });
        break;
      }
      case 'shed': {
        // its old skin splits and sloughs off in pale flakes, and it gleams new underneath
        this.burst(TEX.leaf, 0, -side * 0.6, 22, { speed: { min: 15, max: 50 }, angle: { min: 0, max: 360 }, gravityY: 40, lifespan: { min: 900, max: 1500 }, rotate: { min: 0, max: 360 }, alpha: { start: 1, end: 0 }, tint: [0xd8d0a0, 0xc8c090, 0xe8e0b8] });
        this.flash(0, -side * 0.55, side * 1.6, 0x8aff6a, 700);
        this.gather(0, -side * 0.55, side * 0.9, TEX.px, [0xc8ff9a, 0x8aff6a], ms, false, ADD);
        break;
      }

      // ---- the black dragon ----
      case 'shadowflame': {
        // a long line of black fire, its core dull red, out of its jaws, through the foe and past it
        const { from, to } = this.aim(local);
        const dx = to.x - from.x, dy = to.y - from.y;
        const reach = Math.max(1, Math.hypot(dx, dy));
        const end = { x: from.x + (dx / reach) * Math.max(reach * 1.5, side * 2.5), y: from.y + (dy / reach) * Math.max(reach * 1.5, side * 2.5) };
        const flow = Math.max(320, ms * 0.65);
        const dist = this.dist(from, end);
        const ang = this.spread(from, end, 5);
        const dark = this.emitter(TEX.flame, from.x, from.y, {
          lifespan: { min: 380, max: 520 }, speed: { min: dist * 1.9, max: dist * 2.3 }, angle: ang,
          scale: { start: 1.2, end: 2.6 }, alpha: { start: 0.95, end: 0 }, tint: BLACKFIRE, blendMode: NORMAL, frequency: 10, quantity: 3,
        });
        const core = this.emitter(TEX.flame, from.x, from.y, {
          lifespan: { min: 360, max: 480 }, speed: { min: dist * 2.0, max: dist * 2.4 }, angle: this.spread(from, end, 3),
          scale: { start: 0.7, end: 1.2 }, alpha: { start: 0.9, end: 0 }, color: BLACKFIRE_CORE, colorEase: 'Quad.easeIn', blendMode: ADD, frequency: 16, quantity: 2,
        });
        scene.time.delayedCall(flow, () => { dark?.stop(); core?.stop(); });
        // the line burns along its length after it
        for (let i = 1; i <= 4; i++) {
          const k = i / 4;
          scene.time.delayedCall(flow * (0.3 + 0.12 * i), () => {
            const p = { x: from.x + (end.x - from.x) * k, y: from.y + (end.y - from.y) * k };
            this.flash(p.x, p.y, side * 0.9, 0x8a1c3a, 380);
            this.puff(p.x, p.y, 4, this.look.dust, 0.55);
          });
        }
        scene.time.delayedCall(flow * 0.55, () => playSfx('huge.fire_hit', { space }));
        scene.tweens.add({ targets: this.host.bodies(), x: `+=${-Math.sign(dx || 1) * 2}`, duration: 120, yoyo: true, repeat: 2 });
        this.shake(Math.max(300, flow), 0.003);
        break;
      }
      case 'cinders': {
        // the sky over the foe rains dull embers and black ash
        const to = local ?? { x: this.headSide() * side, y: -side * 0.3 };
        const embers = this.emitter(TEX.flame, to.x, to.y - side * 3, {
          lifespan: { min: 380, max: 520 }, speedY: { min: side * 4, max: side * 6 }, speedX: { min: -side * 0.6, max: side * 0.6 }, scale: { start: 1.3, end: 0.8 },
          color: [0xff8a3a, 0xd04a1a, 0x8a1c0c, 0x2a0604], blendMode: ADD, frequency: 26, quantity: 2, emitZone: this.zone(-side * 0.8, 0, side * 1.6, 4),
        });
        scene.time.delayedCall(Math.max(300, ms * 0.5), () => embers?.stop());
        scene.time.delayedCall(Math.max(220, ms * 0.38), () => {
          this.ring(to.x, to.y, side * 2.2, 0x5a1410, 700);
          this.flash(to.x, to.y, side * 1.6, 0xb03a1a, 460);
          this.puff(to.x, to.y, 12, this.look.dust, 0.6);
          this.shake(260, 0.004);
          playSfx('huge.fire_hit', { space });
        });
        break;
      }
      case 'smoke': {
        // black smoke billows out of it and the darkness round it swells
        this.puff(0, -side * 0.5, 28, look.dust, 0.75, 1.8);
        if (this.shade) {
          const w = this.shade.displayWidth, h = this.shade.displayHeight;
          scene.tweens.add({ targets: this.shade, displayWidth: w * 1.5, displayHeight: h * 1.4, duration: Math.max(300, ms * 0.5), yoyo: true, hold: ms, ease: 'Sine.easeOut' });
        }
        this.flare(this.spark, 1, 500);
        playSfx('spell.smoke', { space });
        break;
      }
      case 'eclipse': {
        // the light goes out: darkness swallows the field round it, its eye burning in it, embers whirling
        const dark = this.shadow(0, -side * 0.5, side * 1.2, side * 0.9, 0.0, 0);
        if (dark) {
          scene.tweens.add({
            targets: this.gone(dark), displayWidth: side * 6, displayHeight: side * 4.4, alpha: 0.82, duration: Math.max(260, ms * 0.4), ease: 'Quad.easeOut',
            onComplete: () => scene.tweens.add({ targets: dark, alpha: 0, delay: ms * 0.8, duration: 900, onComplete: () => dark.destroy() }),
          });
        }
        this.flare(this.spark, 1, 1400);
        this.ring(0, -side * 0.5, side * 3.2, 0x8a1c0c, 900);
        this.burst(TEX.px, 0, -side * 0.5, 30, { speed: { min: 30, max: 110 }, angle: { min: 0, max: 360 }, lifespan: { min: 800, max: 1400 }, scale: { start: 1, end: 0 }, color: [0xff6a2a, 0xd04a1a, 0x8a1c0c], blendMode: ADD });
        this.shake(520, 0.005);
        break;
      }

      // ---- the great eagle (docs/95) ----
      case 'stoop': {
        // it climbs, folds and drops on the foe: a streak of air down to it, a shriek, feathers bursting
        const to = local ?? { x: this.headSide() * side * 1.2, y: -side * 0.3 };
        const dir = Math.sign(to.x) || this.headSide();
        const up = Math.max(140, ms * 0.28);
        if (!this.q.still) {
          scene.tweens.chain({
            targets: this.host.bodies(),
            tweens: [
              { y: '-=12', duration: up, ease: 'Quad.easeOut' },
              { y: '+=16', x: `+=${dir * side * 0.35}`, duration: up * 0.5, ease: 'Quad.easeIn' },
              { y: '-=4', x: `-=${dir * side * 0.35}`, duration: up * 1.2, ease: 'Sine.easeInOut' },
            ],
          });
        }
        const head = this.onArt(this.look.head);
        this.ring(head.x, head.y, side * 1.6, 0xffffff, 420);
        scene.time.delayedCall(up * 0.9, () => this.streak({ x: to.x - dir * side * 1.2, y: to.y - side * 2.4 }, to, 0xf4f8ff, Math.max(160, ms * 0.3)));
        scene.time.delayedCall(up * 1.5, () => {
          this.slash(to, 0xfff0c0);
          this.ring(to.x, to.y, side * 1.8, look.ring, 480);
          this.burst(TEX.leaf, to.x, to.y, 18, { speed: { min: 50, max: 130 }, angle: { min: 0, max: 360 }, gravityY: 40, lifespan: { min: 700, max: 1200 }, rotate: { min: 0, max: 360 }, tint: FEATHERS });
          this.puff(to.x, to.y, 8, look.dust, 0.45);
          this.shake(240, 0.004);
          playSfx('spell.force_hit', { space });
        });
        break;
      }
      case 'gale': {
        // its wings sweep: gusts of wind roll out from it, streaks and feathers flung on them
        this.ring(0, -side * 0.4, side * 2.6, look.ring, 600);
        scene.time.delayedCall(180, () => this.ring(0, -side * 0.4, side * 3.2, look.ring, 700));
        const { from, to } = this.aim(local, { x: 0, y: -0.5 });
        const dist = this.dist(from, to);
        const flow = Math.max(320, ms * 0.6);
        const wind = this.emitter(TEX.px, from.x, from.y, {
          lifespan: { min: 380, max: 560 }, speed: { min: dist * 1.6, max: dist * 2.4 }, angle: this.spread(from, to, 30),
          scaleX: { start: 2, end: 8 }, scaleY: 0.5, alpha: { start: 0.7, end: 0 }, tint: [0xffffff, 0xe8f0f4], blendMode: NORMAL, frequency: 10, quantity: 3,
        });
        const plumes = this.emitter(TEX.leaf, from.x, from.y, {
          lifespan: { min: 500, max: 800 }, speed: { min: dist * 1.2, max: dist * 2 }, angle: this.spread(from, to, 34), rotate: { min: 0, max: 360 }, alpha: { start: 1, end: 0 }, tint: FEATHERS, frequency: 40,
        });
        scene.time.delayedCall(flow, () => { wind?.stop(); plumes?.stop(); });
        scene.tweens.add({ targets: this.host.bodies(), y: '-=6', duration: Math.max(120, ms * 0.2), yoyo: true, repeat: 1, ease: 'Sine.easeOut' });
        scene.time.delayedCall(flow * 0.5, () => { this.puff(to.x, to.y, 10, look.dust, 0.5); this.ring(to.x, to.y, side * 1.6, look.ring, 500); });
        this.shake(Math.max(260, flow), 0.002);
        playSfx('spell.thunder_cast', { space });
        break;
      }
      case 'screech': {
        this.screech(local, ms);
        break;
      }

      // ---- the ent (docs/95) ----
      case 'roots': {
        // the ground round the foe splits and roots burst up out of it, curl in and grab
        const to = local ?? { x: this.headSide() * side * 1.2, y: -side * 0.2 };
        const foot = { x: to.x, y: to.y + side * 0.15 };
        this.crack(foot.x, foot.y, side * 0.7, 1400);
        this.roots(foot, side * 0.75, ms * 1.6);
        scene.time.delayedCall(Math.max(160, ms * 0.3), () => {
          this.ringIn(foot.x, foot.y, side * 1.8, 0x4a6a24, 420);
          this.puff(foot.x, foot.y, 10, look.dust, 0.5, 1.4, true);
          this.burst(TEX.shard, foot.x, foot.y, 10, { speed: { min: 40, max: 90 }, angle: { min: 220, max: 320 }, gravityY: 240, lifespan: { min: 400, max: 700 }, rotate: { min: 0, max: 360 }, tint: look.dust });
          this.shake(300, 0.004);
          playSfx('spell.nature', { space });
        });
        break;
      }
      case 'thorns': {
        // a line of thorny brush springs up from its feet to the foe and past it, one clump after another
        const to = local ?? { x: this.headSide() * side * 2, y: -side * 0.1 };
        const start = { x: this.headSide() * side * 0.35, y: -side * 0.1 };
        const dx = to.x - start.x, dy = to.y + side * 0.1 - start.y;
        const n = 7;
        for (let i = 0; i <= n; i++) {
          const k = (i / n) * 1.25;
          const p = { x: start.x + dx * k, y: start.y + dy * k };
          scene.time.delayedCall(i * Math.max(40, ms * 0.07), () => this.bramble(p, ms * 1.4));
        }
        scene.time.delayedCall(Math.max(200, ms * 0.45), () => { this.flash(to.x, to.y, side * 1.0, 0x8ac83a, 360); playSfx('spell.nature_cast', { space }); });
        break;
      }
      case 'uproot': {
        // it tears up a log and hurls it end over end at the foe: it lands in a spray of splinters
        const from = this.onArt(BOUGH);
        const to = local ?? { x: this.headSide() * side * 2, y: -side * 0.3 };
        this.puff(from.x, 0, 8, look.dust, 0.5, 1.4, true);
        this.burst(TEX.shard, from.x, -2, 8, { speed: { min: 30, max: 70 }, angle: { min: 220, max: 320 }, gravityY: 200, lifespan: { min: 400, max: 700 }, tint: look.dust });
        const fly = Math.max(260, ms * 0.5);
        this.hurlLog(from, to, fly);
        scene.tweens.add({ targets: this.host.bodies(), x: `+=${(Math.sign(to.x - from.x) || this.headSide()) * 4}`, duration: Math.max(80, ms * 0.15), yoyo: true, ease: 'Quad.easeOut' });
        scene.time.delayedCall(fly, () => {
          this.burst(TEX.shard, to.x, to.y, 18, { speed: { min: 50, max: 130 }, angle: { min: 0, max: 360 }, gravityY: 220, lifespan: { min: 500, max: 900 }, scale: { start: 1.1, end: 0.5 }, rotate: { min: 0, max: 360 }, tint: look.shard });
          this.burst(TEX.leaf, to.x, to.y, 10, { speed: { min: 30, max: 80 }, angle: { min: 0, max: 360 }, gravityY: 60, lifespan: { min: 700, max: 1100 }, rotate: { min: 0, max: 360 }, tint: LEAVES });
          this.ring(to.x, to.y, side * 1.8, look.ring, 480);
          this.puff(to.x, to.y, 10, look.dust, 0.55);
          this.shake(300, 0.005);
          playSfx('huge.debris', { space });
        });
        break;
      }
      case 'leafstorm': {
        // its crown thrashes: a whirlwind of leaves torn off it, spiralling out over the field, its eyes blazing
        this.flare(this.spark, 1, 900);
        this.flare(this.glow, 0.5, 900);
        for (let i = 0; i < 3; i++) {
          scene.time.delayedCall(i * 160, () => this.burst(TEX.leaf, 0, -side * 0.8, 22, {
            speed: { min: 50, max: 140 }, angle: { min: 0, max: 360 }, gravityY: 20, lifespan: { min: 900, max: 1500 }, rotate: { start: 0, end: 720 }, alpha: { start: 1, end: 0 }, tint: LEAVES,
          }));
        }
        for (let i = 0; i < 3; i++) scene.time.delayedCall(i * 200, () => this.ring(0, -side * 0.2, side * (2.4 + i * 0.6), look.ring, 700));
        if (local) scene.time.delayedCall(Math.max(200, ms * 0.4), () => { this.burst(TEX.leaf, local.x, local.y, 14, { speed: { min: 20, max: 70 }, angle: { min: 0, max: 360 }, lifespan: { min: 600, max: 1000 }, rotate: { min: 0, max: 360 }, tint: LEAVES }); this.slash(local, 0x9ad84a); });
        scene.tweens.add({ targets: this.host.bodies(), x: '+=1', duration: 50, yoyo: true, repeat: 5 });
        this.shake(500, 0.004);
        playSfx('spell.nature_cast', { space });
        break;
      }

      // ---- the grim giant (docs/95) ----
      case 'cleave': {
        // a great flat swing of its club across everything in front of it: an arc of air, those in it staggered
        const dir = local ? Math.sign(local.x) || this.headSide() : this.headSide();
        const to = local ?? { x: dir * side * 1.1, y: -side * 0.3 };
        this.lunge(to, 6, ms);
        this.glint(this.onArt(CLUB), RUNE, ms * 0.5);
        const swing = Math.max(160, ms * 0.3);
        this.arc({ x: dir * side * 0.45, y: -side * 0.35 }, side * 1.15, dir, swing, ms);
        scene.time.delayedCall(swing * 0.8, () => {
          this.flash(to.x, to.y, side * 1.2, RUNE, 360);
          this.burst(TEX.shard, to.x, to.y, 10, { speed: { min: 40, max: 100 }, angle: { min: 200, max: 340 }, gravityY: 240, lifespan: { min: 400, max: 700 }, rotate: { min: 0, max: 360 }, tint: look.shard });
          this.puff(to.x, to.y, 8, look.dust, 0.5);
          this.stars(to, ms * 1.6);
          this.shake(300, 0.005);
          playSfx('huge.debris', { space });
        });
        break;
      }
      case 'earthsplit': {
        // the club comes down and a glowing crack runs from it along the ground through the foe and on
        const start = { x: this.headSide() * side * 0.5, y: -side * 0.1 };
        const to = local ?? { x: this.headSide() * side * 2.2, y: -side * 0.1 };
        const dx = to.x - start.x, dy = to.y + side * 0.1 - start.y;
        const reach = Math.max(1, Math.hypot(dx, dy));
        const end = { x: start.x + (dx / reach) * Math.max(reach * 1.4, side * 2.4), y: start.y + (dy / reach) * Math.max(reach * 1.4, side * 2.4) };
        this.lunge(to, 5, ms);
        this.glint(this.onArt(CLUB), RUNE, ms * 0.5);
        const run = Math.max(260, ms * 0.5);
        scene.time.delayedCall(Math.max(100, ms * 0.15), () => {
          this.ring(start.x, start.y, side * 1.4, look.ring, 400);
          this.shake(Math.max(300, run), 0.005);
          this.fissure(start, end, run, ms * 1.8);
          for (let i = 1; i <= 5; i++) {
            const k = i / 5;
            scene.time.delayedCall(run * k, () => {
              const p = { x: start.x + (end.x - start.x) * k, y: start.y + (end.y - start.y) * k };
              this.burst(TEX.shard, p.x, p.y, 6, { speed: { min: 40, max: 100 }, angle: { min: 230, max: 310 }, gravityY: 260, lifespan: { min: 400, max: 700 }, rotate: { min: 0, max: 360 }, tint: look.shard });
              this.puff(p.x, p.y, 3, look.dust, 0.4);
            });
          }
          playSfx('spell.earth_burst', { space });
        });
        scene.time.delayedCall(Math.max(100, ms * 0.15) + run * 0.6, () => { this.flash(to.x, to.y, side * 1.4, RUNE, 420); this.ring(to.x, to.y, side * 1.8, look.ring, 520); });
        break;
      }
      case 'runes': {
        // runes flare in a ring round it, then a pulse of cold light rolls out of the ring
        this.flare(this.spark, 1, 900);
        this.flare(this.glow, 0.55, 900);
        this.runeRing(side * 0.85, ms);
        scene.time.delayedCall(Math.max(200, ms * 0.45), () => {
          this.ring(0, -side * 0.45, side * 3.2, RUNE, 800);
          scene.time.delayedCall(160, () => this.ring(0, -side * 0.45, side * 2.4, 0xffffff, 600));
          this.burst(TEX.px, 0, -side * 0.5, 24, { speed: { min: 30, max: 100 }, angle: { min: 0, max: 360 }, lifespan: { min: 600, max: 1000 }, scale: { start: 1.2, end: 0 }, color: [0xffffff, RUNE], blendMode: ADD });
          if (local) this.sigil(local, RUNE, ms * 1.2);
          playSfx('spell.frost_cast', { space });
        });
        break;
      }

      // ---- the kraken (docs/116) ----
      case 'tide': {
        // the water heaves up round it and a wave surges out to the foe, where it turns into a whirlpool
        const to = local ?? { x: this.headSide() * side * 1.8, y: -side * 0.2 };
        const dir = Math.sign(to.x) || this.headSide();
        const from = { x: dir * side * 0.45, y: -side * 0.15 };
        const run = Math.max(260, ms * 0.45);
        this.ring(0, -side * 0.15, side * 2.2, look.ring, 520);
        this.splash(from.x, from.y, 0.9);
        const spray = this.emitter(TEX.drop, from.x, from.y, {
          lifespan: { min: 380, max: 560 }, speed: { min: this.dist(from, to) * 1.5, max: this.dist(from, to) * 2.1 }, angle: this.spread(from, to, 14),
          gravityY: 120, scale: { start: 1, end: 0.6 }, alpha: { start: 1, end: 0 }, tint: [...FOAM, ...BRINE], blendMode: NORMAL, frequency: 12, quantity: 3,
        });
        scene.time.delayedCall(run, () => spray?.stop());
        // the crest rolling along the water
        for (let i = 1; i <= 5; i++) {
          const k = i / 5;
          scene.time.delayedCall(run * k * 0.8, () => {
            const p = { x: from.x + (to.x - from.x) * k, y: from.y + (to.y + side * 0.1 - from.y) * k };
            this.ring(p.x, p.y, side * 0.9, 0xe8f8fa, 420);
            this.burst(TEX.px, p.x, p.y, 6, { speed: { min: 20, max: 60 }, angle: { min: 220, max: 320 }, gravityY: 160, lifespan: { min: 300, max: 600 }, tint: FOAM });
          });
        }
        scene.time.delayedCall(run * 0.85, () => {
          const foot = { x: to.x, y: to.y + side * 0.15 };
          this.whirl(foot, side * 0.8, 0xe8f8fa, ms * 1.6);
          for (let i = 0; i < 3; i++) scene.time.delayedCall(i * 140, () => this.ringIn(foot.x, foot.y, side * (2.2 - i * 0.4), i % 2 ? 0x2a6a8a : 0xc8f0f4, 460));
          this.gather(foot.x, foot.y, side * 1.1, TEX.px, FOAM, ms, true);
          this.splash(to.x, to.y, 1.1);
          this.shake(360, 0.005);
          playSfx('cue.water_rush', { space });
        });
        scene.tweens.add({ targets: this.host.bodies(), x: `+=${dir * 3}`, duration: Math.max(100, ms * 0.2), yoyo: true, ease: 'Sine.easeOut' });
        break;
      }
      case 'ink': {
        // a jet of black ink at the foe blooming into a cloud over it, and the kraken veiled in its own
        const { from, to } = this.aim(local);
        const flow = Math.max(260, ms * 0.45);
        const jet = this.emitter(TEX.smoke, from.x, from.y, {
          lifespan: { min: 380, max: 560 }, speed: { min: this.dist(from, to) * 1.4, max: this.dist(from, to) * 2 }, angle: this.spread(from, to, 14),
          rotate: { min: 0, max: 360 }, scale: { start: 0.4, end: 1.8 }, alpha: { start: 0.85, end: 0 }, tint: INK, blendMode: NORMAL, frequency: 14, quantity: 3,
        });
        scene.time.delayedCall(flow, () => jet?.stop());
        scene.time.delayedCall(flow * 0.6, () => {
          this.shadow(to.x, to.y + side * 0.1, side * 2, side * 1.1, 0.75, ms * 2.2);
          this.puff(to.x, to.y, 22, INK, 0.85, 2.2);
          this.ring(to.x, to.y, side * 1.8, 0x1a1a2a, 600);
          playSfx('spell.shadow', { space });
        });
        // and over itself: the ink pooled round it swells, a cloud rising off it
        this.puff(0, -side * 0.5, 22, INK, 0.75, 2.0);
        if (this.shade) {
          const w = this.shade.displayWidth, h = this.shade.displayHeight;
          scene.tweens.add({ targets: this.shade, displayWidth: w * 1.5, displayHeight: h * 1.4, duration: Math.max(300, ms * 0.5), yoyo: true, hold: ms, ease: 'Sine.easeOut' });
        }
        this.flare(this.spark, 1, 600);
        this.flare(this.twin, 1, 600);
        break;
      }
      case 'grasp': {
        // the arm nearest the foe lashes out over the water, slaps down on it in a splash and drags back
        const to = local ?? { x: this.headSide() * side * 1.6, y: -side * 0.3 };
        const shoot = Math.max(120, ms * 0.22);
        this.tentacle(this.armToward(to, 0), to, 0x3a2a3a, 0xc89a8a, shoot, Math.max(200, ms * 0.6), 5);
        scene.time.delayedCall(shoot, () => {
          this.splash(to.x, to.y + side * 0.1, 0.8);
          this.ring(to.x, to.y + side * 0.1, side * 1.4, look.ring, 420);
          this.shake(220, 0.004);
          playSfx('cue.splash', { space });
        });
        break;
      }
      case 'regrow': {
        // the water is drawn in to it, the stump writhes and a new arm curls out of it, its wounds closing
        this.gather(0, -side * 0.4, side * 1.2, TEX.px, FOAM, ms, true);
        const tip = ARM_TIPS[this.host.unitId % ARM_TIPS.length]!;
        const p = this.onArt(tip);
        scene.time.delayedCall(Math.max(200, ms * 0.45), () => {
          this.tentacle({ x: p.x * 0.3, y: p.y * 0.6 }, p, 0x3a2a3a, 0xc89a8a, Math.max(160, ms * 0.3), ms * 0.6, 4);
          this.flash(0, -side * 0.5, side * 1.8, 0x5ad8b8, 600);
          this.burst(TEX.px, 0, -side * 0.5, 18, { speed: { min: 10, max: 50 }, angle: { min: 0, max: 360 }, lifespan: { min: 500, max: 900 }, scale: { start: 1.1, end: 0 }, color: [0xffffff, 0x9af0d8], blendMode: ADD });
          playSfx('spell.heal', { space });
        });
        break;
      }

      // ---- the maw of the deep (docs/116) ----
      case 'gulp': {
        // it lurches onto the foe and its jaws snap shut over it: a wet burst, drool and brine flying
        const to = local ?? { x: this.headSide() * side * 1.2, y: -side * 0.4 };
        this.lunge(to, 12, ms);
        const snap = Math.max(160, ms * 0.3);
        this.jaws(to, snap, ms);
        scene.time.delayedCall(snap, () => {
          this.flash(to.x, to.y, side * 1.3, ABYSS, 420);
          this.burst(TEX.drop, to.x, to.y, 18, { speed: { min: 40, max: 110 }, angle: { min: 0, max: 360 }, gravityY: 220, lifespan: { min: 400, max: 800 }, scale: { start: 1, end: 0.6 }, tint: [...DROOL, ...BRINE] });
          this.splash(to.x, to.y + side * 0.15, 0.7, true);
          this.shake(280, 0.005);
          playSfx('cue.splash', { space });
        });
        break;
      }
      case 'abyss': {
        // a roar from the bottom of the sea: violet shock rings out of its mouth, the black water heaving
        const mouth = this.onArt(MAW_MOUTH);
        this.flare(this.glow, 0.7, 900);
        this.flare(this.spark, 1, 900);
        this.flash(mouth.x, mouth.y, side * 1.8, ABYSS, 600);
        for (let i = 0; i < 3; i++) scene.time.delayedCall(i * 150, () => this.ring(mouth.x, mouth.y, side * (3.4 - i * 0.5), i % 2 ? 0x6a2ab0 : ABYSS, 760));
        this.ring(0, -side * 0.1, side * 2.6, 0x0a0610, 700);
        this.burst(TEX.drop, mouth.x, mouth.y, 14, { speed: { min: 50, max: 120 }, angle: { min: 0, max: 360 }, gravityY: 200, lifespan: { min: 400, max: 800 }, tint: DROOL });
        this.manyEyes(20, ms);
        if (local) scene.time.delayedCall(Math.max(160, ms * 0.3), () => { this.flash(local.x, local.y, side * 1.2, ABYSS, 420); this.stars(local, ms * 1.4); });
        scene.tweens.add({ targets: this.host.bodies(), x: '+=1', duration: 45, yoyo: true, repeat: 7 });
        this.shake(520, 0.006);
        playSfx('spell.necrotic_cast', { space });
        break;
      }
      case 'tongue': {
        // a purple tentacle-tongue shoots from its mouth to the foe, catches and reels back in
        const { from, to } = this.aim(local, MAW_MOUTH);
        const shoot = Math.max(100, ms * 0.18);
        this.tentacle(from, to, 0x5a1a4a, 0xd06aa8, shoot, Math.max(180, ms * 0.55), 4);
        scene.time.delayedCall(shoot, () => {
          this.flash(to.x, to.y, side * 0.9, 0xd06aa8, 320);
          this.burst(TEX.drop, to.x, to.y, 10, { speed: { min: 30, max: 80 }, angle: { min: 200, max: 340 }, gravityY: 260, lifespan: { min: 300, max: 600 }, tint: DROOL });
          playSfx('spell.acid_hit', { space });
        });
        break;
      }
      case 'sink': {
        // it slides down into the black water: the ring of dark swells over it, bubbles, its eyes still showing
        if (!this.q.still) scene.tweens.add({ targets: this.host.bodies(), y: '+=10', alpha: 0.45, duration: Math.max(200, ms * 0.35), yoyo: true, hold: ms * 0.6, ease: 'Quad.easeIn' });
        this.ringIn(0, -side * 0.15, side * 2.6, 0x0a0610, 520);
        this.splash(0, -side * 0.15, 0.8, true);
        this.bubbles(0, -side * 0.3, 24);
        if (this.shade) {
          const w = this.shade.displayWidth, h = this.shade.displayHeight;
          scene.tweens.add({ targets: this.shade, displayWidth: w * 1.4, displayHeight: h * 1.3, alpha: 0.75, duration: Math.max(300, ms * 0.4), yoyo: true, hold: ms * 0.6, ease: 'Sine.easeOut' });
        }
        scene.time.delayedCall(Math.max(200, ms * 0.4), () => this.manyEyes(10, ms));
        playSfx('cue.splash', { space });
        break;
      }
      case 'eyes': {
        // every eye on it opens and turns on one hero: orange lights flaring across its back, a mark on the foe
        const eye = this.onArt(EYE.maw ?? MAW_BACK);
        const to = local ?? { x: this.headSide() * side * 1.5, y: -side * 0.4 };
        this.flare(this.spark, 1, 700);
        this.manyEyes(28, ms);
        this.glint(eye, 0xffb02a, ms);
        scene.time.delayedCall(Math.max(160, ms * 0.3), () => {
          this.ring(eye.x, eye.y, side * 2.2, 0xff8a2a, 520);
          this.sigil(to, 0xff8a2a, ms * 1.4);
          playSfx('spell.mark', { space });
        });
        break;
      }
    }
  }

  /** It falls: the aura goes out, each its own way. */
  fall(ms: number): void {
    if (this.dead || this.down) return;
    this.down = true;
    for (const e of this.emitters) e.stop();
    for (const t of this.timers) t.remove();
    const side = this.side;
    const fade = Math.max(200, ms);
    this.hover(0);
    for (const p of [this.glow, this.ground, this.spark, this.shade, this.twin, this.foam]) if (p) this.host.scene.tweens.add({ targets: p, alpha: 0, duration: fade * 1.5 });
    if (this.q.still) return;
    const look = this.look;
    switch (this.kind) {
      case 'dragon':
        this.burst(TEX.flame, 0, -side * 0.5, 30, { speed: { min: 20, max: 80 }, angle: { min: 0, max: 360 }, lifespan: { min: 700, max: 1400 }, scale: { start: 1, end: 0 }, color: [0xffd060, 0xf05a14, 0x3a0a04], blendMode: ADD });
        break;
      case 'troll':
        this.puff(0, -side * 0.2, 22, [0x9a8a72, 0x7a6c58], 0.6, 1.6, true);
        break;
      case 'yeti':
        // it goes down in a last flurry, and the cold goes out of the air
        this.burst(TEX.flake, 0, -side * 0.5, 30, { speed: { min: 20, max: 80 }, angle: { min: 0, max: 360 }, gravityY: 30, lifespan: { min: 900, max: 1600 }, rotate: { min: 0, max: 360 }, alpha: { start: 1, end: 0 }, tint: look.dust });
        this.puff(0, -side * 0.2, 14, look.dust, 0.5, 1.6, true);
        break;
      case 'worm':
        // the sand pours off it and closes over where it lay
        this.burst(TEX.px, 0, -side * 0.6, 40, { speed: { min: 10, max: 50 }, angle: { min: 180, max: 360 }, gravityY: 220, lifespan: { min: 600, max: 1100 }, tint: look.dust });
        this.puff(0, -side * 0.1, 18, look.dust, 0.55, 1.6, true);
        this.ringIn(0, -side * 0.1, side * 2.2, look.ring, fade);
        break;
      case 'spider':
        // the brood scatters, every one of them, away from her
        this.burst(TEX.dot, 0, -side * 0.25, 40, { speed: { min: 60, max: 160 }, angle: { min: 0, max: 360 }, lifespan: { min: 800, max: 1400 }, alpha: { start: 1, end: 0, ease: 'Expo.easeIn' }, tint: [0x140c12, 0x2a1420, 0x3a0c14] });
        break;
      case 'serpent':
        // its eye dims, and the swamp mist closes over it
        this.puff(0, -side * 0.3, 18, [0x7a8f6a, 0x5f7350, 0x9aa88a], 0.45, 1.8, true);
        break;
      case 'wyrm':
        // it comes apart into smoke and the last of its embers
        this.puff(0, -side * 0.5, 26, look.dust, 0.75, 2.0);
        this.burst(TEX.px, 0, -side * 0.5, 24, { speed: { min: 20, max: 70 }, angle: { min: 200, max: 340 }, lifespan: { min: 900, max: 1600 }, scale: { start: 0.9, end: 0 }, color: [0xd04a1a, 0x8a1c0c, 0x3a0806], blendMode: ADD });
        break;
      case 'eagle':
        // it comes down out of the air in a storm of its own feathers
        this.burst(TEX.leaf, 0, -side * 0.6, 34, { speed: { min: 20, max: 90 }, angle: { min: 0, max: 360 }, gravityY: 30, lifespan: { min: 1000, max: 1700 }, rotate: { min: 0, max: 360 }, alpha: { start: 1, end: 0 }, tint: FEATHERS });
        this.puff(0, -side * 0.1, 12, look.dust, 0.45, 1.6, true);
        break;
      case 'ent':
        // the green goes out of its eyes and its leaves come down all at once
        this.burst(TEX.leaf, 0, -side * 0.9, 40, { speed: { min: 10, max: 60 }, angle: { min: 0, max: 360 }, gravityY: 50, lifespan: { min: 1200, max: 2000 }, rotate: { min: 0, max: 360 }, alpha: { start: 1, end: 0 }, tint: LEAVES });
        this.puff(0, -side * 0.2, 16, look.dust, 0.5, 1.6, true);
        break;
      case 'giant':
        // it goes down like a falling hold: snow and frost thrown up, the last runes winking out
        this.puff(0, -side * 0.2, 20, look.dust, 0.6, 1.8, true);
        this.burst(TEX.px, 0, -side * 0.5, 16, { speed: { min: 10, max: 50 }, angle: { min: 0, max: 360 }, lifespan: { min: 600, max: 1100 }, scale: { start: 1, end: 0 }, color: [0xffffff, RUNE], blendMode: ADD });
        break;
      case 'kraken':
        // docs/116: it sinks: a last cloud of ink, bubbles boiling up, the foam closing over where it lay
        this.puff(0, -side * 0.4, 24, INK, 0.8, 2.0, true);
        this.bubbles(0, -side * 0.4, 36);
        this.ringIn(0, -side * 0.1, side * 2.4, look.ring, fade);
        break;
      case 'maw':
        // it goes under: bubbles and black water, its eyes winking out one by one
        this.puff(0, -side * 0.3, 18, [0x0a0610, 0x1a1024, 0x2a1a3a], 0.75, 1.8, true);
        this.bubbles(0, -side * 0.4, 30);
        this.manyEyes(18, fade);
        this.ringIn(0, -side * 0.1, side * 2.4, 0x0a0610, fade);
        break;
    }
  }

  // ---- helpers ---------------------------------------------------------------------------------

  /** The side its head is on, the way it faces: -1 west, 1 east (docs/95: by which way its art faces as drawn, and whether it is flipped). */
  private headSide(): number {
    const drawn = this.host.art.faces === 'right' ? 1 : -1;
    return this.host.body.flipX ? -drawn : drawn;
  }

  /** docs/102 FL3: the body is `px` off the ground in flight — the ground it lays stays on the ground. */
  setLift(px: number): void {
    if (!this.ground || this.down || this.dead) return;
    // its ground stays under it, and fades while it is up: the flight's shadow is its mark there
    this.ground.y = -this.side / 2 + px;
    this.ground.setAlpha((this.look.additive ? 0.9 : 0.85) * (1 - Math.min(1, px / (this.side * 0.6))));
  }

  /** The eagle hovers: its bodies lifted `px` art pixels off their feet point (the origin, so walks and lunges are left be). */
  private hover(px: number): void {
    if (this.kind !== 'eagle') return;
    const base = bodyOriginY(this.host.art.key);
    const h = this.host.art.h;
    for (const b of this.host.bodies()) if (b.texture.key === this.host.art.key) b.setOrigin(0.5, base + px / h);
  }

  /** A point on the art (fractions of it from its feet point, as drawn) in the container's space. */
  private onArt(p: Pt): Pt {
    const b = this.host.body;
    return { x: (b.flipX ? -p.x : p.x) * b.displayWidth, y: p.y * b.displayHeight };
  }

  /** From its head (or `mouth` on the art) to the foe, or out ahead of it when it aims at no one. */
  private aim(local: Pt | undefined, mouth: Pt = this.look.head): { from: Pt; to: Pt } {
    const from = this.onArt(mouth);
    const to = local ?? { x: from.x + this.headSide() * this.side * 2, y: from.y + this.side * 0.4 };
    return { from, to };
  }

  private dist(a: Pt, b: Pt): number { return Math.max(this.side, Math.hypot(b.x - a.x, b.y - a.y)); }

  private spread(a: Pt, b: Pt, deg: number): { min: number; max: number } {
    const ang = Phaser.Math.RadToDeg(Math.atan2(b.y - a.y, b.x - a.x));
    return { min: ang - deg, max: ang + deg };
  }

  /** A cone of particles from its head to the foe and past it; returns where it went and how long it flowed. */
  private cone(local: Pt | undefined, ms: number, o: { tex: string; spread: number; scale: EmitterCfg['scale']; rotate?: EmitterCfg['rotate']; tint: number[]; blend?: number }): { from: Pt; to: Pt; flow: number } {
    const { from, to } = this.aim(local);
    const dist = this.dist(from, to);
    const flow = Math.max(300, ms * 0.6);
    const e = this.emitter(o.tex, from.x, from.y, {
      lifespan: { min: 420, max: 620 }, speed: { min: dist * 1.6, max: dist * 2.3 }, angle: this.spread(from, to, o.spread),
      scale: o.scale, ...(o.rotate ? { rotate: o.rotate } : {}), alpha: { start: 1, end: 0 }, tint: o.tint, blendMode: o.blend ?? NORMAL, frequency: 12, quantity: 3,
    });
    this.host.scene.time.delayedCall(flow, () => e?.stop());
    return { from, to, flow };
  }

  private zone(x: number, y: number, w: number, h: number) {
    return { type: 'random' as const, source: new Phaser.Geom.Rectangle(x, y, w, h) as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource };
  }

  /** A light it wears: a glow image behind the figure or over it. */
  private light(x: number, y: number, w: number, h: number, colour: number, alpha: number, behind: boolean): Phaser.GameObjects.Image {
    const g = this.host.scene.add.image(x, y, TEX.glow).setTint(colour).setBlendMode(ADD).setAlpha(alpha);
    g.setDisplaySize(w, h);
    if (behind) this.under(g); else this.host.container.add(g);
    this.parts.push(g);
    return g;
  }

  /**
   * A light it wears flares up and settles back. It is free to flare again (and back at its own
   * alpha) by the scene clock, not the tween's end: a new board kills the tween without a word.
   */
  private flare(img: Phaser.GameObjects.Image | undefined, to: number, ms: number): void {
    if (!img || this.q.still || this.held.has(img)) return;
    this.held.add(img);
    const rest = img.alpha;
    this.host.scene.tweens.add({ targets: img, alpha: to, duration: ms * 0.25, yoyo: true, hold: ms * 0.4, ease: 'Quad.easeOut' });
    this.host.scene.time.delayedCall(ms * 0.9 + 30, () => { this.held.delete(img); if (img.active) img.setAlpha(rest); });
  }

  /** A one-shot piece only its tween removes: it goes too if the scene's tweens are killed (board/oneShots). */
  private gone<T extends Phaser.GameObjects.GameObject>(o: T): T { return oneShot(this.host.scene, o); }

  /** A looping emitter it wears (thinned by intensity; none under reduce motion). */
  private emit(tex: string, cfg: EmitterCfg): void {
    const e = this.emitter(tex, 0, 0, cfg);
    if (e) this.emitters.push(e);
  }

  /** A looping emitter at a point on the art, following it when it turns. */
  private emitAt(at: Pt, tex: string, cfg: EmitterCfg): void {
    const p = this.onArt(at);
    const e = this.emitter(tex, p.x, p.y, cfg);
    if (!e) return;
    this.emitters.push(e);
    this.anchored.push({ obj: e, at });
  }

  /** Something it does over and over (thinned by intensity, stopped when it falls; never under reduce motion). */
  private every(ms: number, fn: () => void): void {
    if (this.q.still || this.q.density < 0.15) return;
    const delay = ms / Math.max(0.3, this.q.density);
    this.timers.push(this.host.scene.time.addEvent({ delay, loop: true, callback: () => { if (!this.dead && !this.down) fn(); } }));
  }

  /** Puts a part on the ground over the shader but under the figure and its outline. */
  private under(obj: Phaser.GameObjects.GameObject): void {
    const c = this.host.container;
    const first = this.host.bodies()[0];
    const i = first ? c.getIndex(first) : -1;
    if (i >= 0) c.addAt(obj, i); else c.addAt(obj, 1);
  }

  /** Sets a worn light's alpha, unless it is flaring. */
  private setA(img: Phaser.GameObjects.Image | undefined, a: number): void {
    if (img && !this.held.has(img)) img.setAlpha(a);
  }

  private emitter(tex: string, x: number, y: number, cfg: EmitterCfg): Phaser.GameObjects.Particles.ParticleEmitter | undefined {
    if (this.q.still || this.q.density < 0.15) return undefined;
    const f = typeof cfg.frequency === 'number' ? cfg.frequency / Math.max(0.3, this.q.density) : cfg.frequency;
    const e = this.host.scene.add.particles(x, y, tex, { ...cfg, ...(f !== undefined ? { frequency: f } : {}) });
    this.host.container.add(e);
    this.parts.push(e);
    return e;
  }

  /** A one-shot burst of `n` (thinned by intensity), reaped once its particles are spent. */
  private burst(tex: string, x: number, y: number, n: number, cfg: EmitterCfg): void {
    if (this.q.still || this.q.density < 0.15) return;
    const e = this.host.scene.add.particles(0, 0, tex, { ...cfg, emitting: false });
    this.host.container.add(e);
    e.explode(Math.max(1, Math.round(n * Math.max(0.3, this.q.density))), x, y);
    this.host.scene.time.delayedCall(1800, () => e.destroy());
  }

  /** A puff of smoke, dust, snow or sand rolling out from a point. */
  private puff(x: number, y: number, n: number, tint: number[], alpha: number, grow = 1.4, low = false): void {
    this.burst(TEX.smoke, x, y, n, {
      speed: { min: 20, max: 80 }, angle: low ? { min: 180, max: 360 } : { min: 0, max: 360 }, lifespan: { min: 700, max: 1300 },
      scale: { start: 0.5, end: grow }, alpha: { start: alpha, end: 0 }, tint,
    });
  }

  /** A plume of sand thrown up from the ground at a point. */
  private spout(x: number, y: number, power: number): void {
    const look = LOOKS.worm;
    this.burst(TEX.px, x, y, Math.round(18 * power), {
      speed: { min: 60 * power, max: 130 * power }, angle: { min: 245, max: 295 }, gravityY: 300, lifespan: { min: 500, max: 900 }, scale: { min: 0.6, max: 1.1 }, tint: look.dust,
    });
    this.burst(TEX.smoke, x, y, Math.round(5 * power), { speed: { min: 10, max: 40 }, angle: { min: 200, max: 340 }, lifespan: { min: 700, max: 1100 }, scale: { start: 0.4, end: 1.2 }, alpha: { start: 0.45, end: 0 }, tint: look.dust });
  }

  /** Particles drawn in from a ring round a point to it (rime closing, sand pouring into a pit). */
  private gather(x: number, y: number, r: number, tex: string, tint: number[], ms: number, low = false, blend: number = NORMAL): void {
    const flow = Math.max(240, ms * 0.55);
    const e = this.emitter(tex, x, y, {
      lifespan: { min: 380, max: 560 }, moveToX: 0, moveToY: 0, scale: { start: 1, end: 0.5 }, alpha: { start: 0.2, end: 1 }, tint, blendMode: blend, frequency: 14, quantity: 3,
      ...(tex === TEX.flake ? { rotate: { min: 0, max: 360 } } : {}),
      emitZone: { type: 'edge', source: new Phaser.Geom.Ellipse(0, 0, r * 2, r * (low ? 0.8 : 1.4)) as unknown as Phaser.Types.GameObjects.Particles.EdgeZoneSource, quantity: 48 },
    });
    this.host.scene.time.delayedCall(flow, () => e?.stop());
  }

  /** A ring of dust or light rolling out over the ground and fading. */
  private ring(x: number, y: number, to: number, colour: number, ms: number): void {
    if (this.q.still) return;
    const r = this.host.scene.add.image(x, y, TEX.ring).setTint(colour).setBlendMode(colour === 0xffffff ? ADD : NORMAL).setAlpha(0.7);
    r.setDisplaySize(this.side * 0.4, this.side * 0.4 * 0.5);
    this.under(r);
    this.host.scene.tweens.add({ targets: this.gone(r), displayWidth: to, displayHeight: to * 0.5, alpha: 0, duration: ms, ease: 'Quad.easeOut', onComplete: () => r.destroy() });
  }

  /** A ring closing in on a point (coils tightening, sand rushing into a pit). */
  private ringIn(x: number, y: number, from: number, colour: number, ms: number): void {
    if (this.q.still) return;
    const r = this.host.scene.add.image(x, y, TEX.ring).setTint(colour).setBlendMode(NORMAL).setAlpha(0.2);
    r.setDisplaySize(from, from * 0.5);
    this.under(r);
    this.host.scene.tweens.add({ targets: this.gone(r), displayWidth: this.side * 0.3, displayHeight: this.side * 0.15, alpha: 0.85, duration: ms, ease: 'Quad.easeIn', onComplete: () => r.destroy() });
  }

  /** A flash of light at a point (a blast landing). */
  private flash(x: number, y: number, size: number, colour: number, ms: number): void {
    if (this.q.still) return;
    const g = this.host.scene.add.image(x, y, TEX.glow).setTint(colour).setBlendMode(ADD).setAlpha(0.9).setDisplaySize(size * 0.5, size * 0.5);
    this.host.container.add(g);
    this.host.scene.tweens.add({ targets: this.gone(g), displayWidth: size, displayHeight: size, alpha: 0, duration: ms, ease: 'Quad.easeOut', onComplete: () => g.destroy() });
  }

  /** A pool of dark on the ground (a pit opening, the light going out); `ms` 0 leaves it to the caller. */
  private shadow(x: number, y: number, w: number, h: number, alpha: number, ms: number): Phaser.GameObjects.Image | undefined {
    if (this.q.still) return undefined;
    const g = this.host.scene.add.image(x, y, TEX.glow).setTint(0x000000).setBlendMode(NORMAL).setAlpha(alpha).setDisplaySize(w, h);
    this.under(g);
    if (ms > 0) {
      g.setScale(g.scaleX * 0.3, g.scaleY * 0.3);
      this.host.scene.tweens.add({ targets: g, scaleX: g.scaleX / 0.3, scaleY: g.scaleY / 0.3, duration: ms * 0.3, ease: 'Quad.easeOut' });
      this.host.scene.tweens.add({ targets: this.gone(g), alpha: 0, delay: ms * 0.7, duration: ms * 0.3, onComplete: () => g.destroy() });
    }
    return g;
  }

  /** A pool of something (venom) spreading on the ground and soaking away. */
  private pool(x: number, y: number, size: number, colour: number, ms: number): void {
    if (this.q.still) return;
    const g = this.host.scene.add.image(x, y, TEX.scorch).setTint(colour).setBlendMode(NORMAL).setAlpha(0.65).setDisplaySize(size * 0.3, size * 0.15);
    this.under(g);
    this.host.scene.tweens.add({ targets: g, displayWidth: size, displayHeight: size * 0.5, duration: ms * 0.25, ease: 'Quad.easeOut' });
    this.host.scene.tweens.add({ targets: this.gone(g), alpha: 0, delay: ms * 0.5, duration: ms * 0.5, onComplete: () => g.destroy() });
  }

  /** The body lunges a few pixels at the foe and back. */
  private lunge(to: Pt, px: number, ms: number): void {
    if (this.q.still) return;
    const dir = Math.sign(to.x) || this.headSide();
    this.host.scene.tweens.add({ targets: this.host.bodies(), x: `+=${dir * px}`, duration: Math.max(80, ms * 0.18), yoyo: true, ease: 'Quad.easeOut' });
  }

  /** Two fang-strokes across the foe. */
  private slash(at: Pt, colour: number): void {
    if (this.q.still) return;
    const g = this.host.scene.add.graphics({ x: at.x, y: at.y }).setBlendMode(ADD);
    this.host.container.add(g);
    const s = this.side * 0.28;
    g.lineStyle(2, colour, 1);
    for (const off of [-s * 0.35, s * 0.35]) {
      g.beginPath(); g.moveTo(off - s * 0.4, -s); g.lineTo(off, 0); g.lineTo(off + s * 0.15, s * 0.6); g.strokePath();
    }
    g.setScale(0.6);
    this.host.scene.tweens.add({ targets: this.gone(g), scale: 1.1, alpha: 0, duration: 380, ease: 'Quad.easeOut', onComplete: () => g.destroy() });
    this.flash(at.x, at.y, this.side * 0.8, colour, 300);
  }

  /** A line of silk shooting from one point to another, hanging, then fading. */
  private silkLine(from: Pt, to: Pt, shoot: number, hold: number): void {
    const g = this.host.scene.add.graphics();
    this.host.container.add(g);
    const draw = (k: number) => {
      g.clear();
      const x = from.x + (to.x - from.x) * k, y = from.y + (to.y - from.y) * k;
      g.lineStyle(1, 0xeeeef8, 0.9); g.beginPath(); g.moveTo(from.x, from.y); g.lineTo(x, y); g.strokePath();
      g.lineStyle(1, 0xffffff, 0.35); g.beginPath(); g.moveTo(from.x, from.y + 1); g.lineTo(x, y + 1); g.strokePath();
    };
    if (this.q.still) { draw(1); this.host.scene.time.delayedCall(hold, () => g.destroy()); return; }
    const st = { k: 0 };
    this.host.scene.tweens.add({ targets: st, k: 1, duration: shoot, ease: 'Quad.easeOut', onUpdate: () => draw(st.k) });
    this.host.scene.tweens.add({ targets: this.gone(g), alpha: 0, delay: hold * 0.6, duration: hold * 0.4, onComplete: () => g.destroy() });
  }

  /** A web net bursting open over a point: spokes and rings of silk. */
  private net(at: Pt, r: number, ms: number): void {
    const g = this.host.scene.add.graphics({ x: at.x, y: at.y });
    this.host.container.add(g);
    const spokes = 8;
    g.lineStyle(1, 0xf0f0fa, 0.9);
    for (let i = 0; i < spokes; i++) {
      const a = (i / spokes) * Math.PI * 2;
      g.beginPath(); g.moveTo(0, 0); g.lineTo(Math.cos(a) * r, Math.sin(a) * r * 0.6); g.strokePath();
    }
    for (const k of [0.35, 0.65, 0.95]) {
      g.lineStyle(1, 0xe0e0f0, 0.75);
      g.beginPath();
      for (let i = 0; i <= spokes; i++) {
        const a = (i / spokes) * Math.PI * 2;
        const sag = i % 2 ? 0.9 : 1;
        const x = Math.cos(a) * r * k * sag, y = Math.sin(a) * r * k * 0.6 * sag;
        if (i) g.lineTo(x, y); else g.moveTo(x, y);
      }
      g.strokePath();
    }
    if (this.q.still) { this.host.scene.time.delayedCall(ms, () => g.destroy()); return; }
    g.setScale(0.2);
    this.host.scene.tweens.add({ targets: g, scale: 1, duration: 220, ease: 'Back.easeOut' });
    this.host.scene.tweens.add({ targets: this.gone(g), alpha: 0, delay: ms * 0.6, duration: ms * 0.4, onComplete: () => g.destroy() });
  }

  /** A glint in an eye: a star of light popping and turning. */
  private glint(at: Pt, colour: number, ms: number): void {
    if (this.q.still) return;
    const s = this.host.scene.add.image(at.x, at.y, TEX.star).setTint(colour).setBlendMode(ADD).setScale(0.2);
    this.host.container.add(s);
    this.host.scene.tweens.add({ targets: this.gone(s), scale: 2.4, angle: 90, duration: Math.max(160, ms * 0.25), yoyo: true, ease: 'Quad.easeOut', onComplete: () => s.destroy() });
    this.flash(at.x, at.y, this.side * 0.9, colour, Math.max(260, ms * 0.4));
  }

  /** An eye-in-a-ring over the one it looks at (the basilisk's gaze falling on it). */
  private sigil(at: Pt, colour: number, ms: number): void {
    if (this.q.still) return;
    const s = this.host.scene.add.image(at.x, at.y - this.side * 0.2, TEX.sigil).setTint(colour).setBlendMode(ADD).setAlpha(0).setDisplaySize(this.side * 0.9, this.side * 0.9);
    this.host.container.add(s);
    this.host.scene.tweens.add({ targets: this.gone(s), alpha: 0.9, duration: ms * 0.25, yoyo: true, hold: ms * 0.3, onComplete: () => s.destroy() });
  }

  /** Cracks splitting out across the ground from a point, then closing up. */
  private crack(x: number, y: number, reach: number, ms: number): void {
    const g = this.host.scene.add.graphics({ x, y });
    this.under(g);
    const arms = 6;
    let seed = this.host.unitId * 97 + Math.round(this.time);
    const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
    const paths: Array<Array<{ x: number; y: number }>> = [];
    for (let a = 0; a < arms; a++) {
      const ang = (a / arms) * Math.PI * 2 + rnd() * 0.6;
      const pts = [{ x: 0, y: 0 }];
      const len = reach * (0.55 + rnd() * 0.45);
      for (let s = 1; s <= 5; s++) {
        const d = (len * s) / 5;
        const wob = (rnd() - 0.5) * 0.7;
        pts.push({ x: Math.cos(ang + wob) * d, y: Math.sin(ang + wob) * d * 0.5 });
      }
      paths.push(pts);
    }
    const draw = (k: number) => {
      g.clear();
      for (const pts of paths) {
        const upto = Math.max(1, Math.ceil(pts.length * Math.min(1, k * 3)));
        g.lineStyle(2, 0x1a120c, 0.85);
        g.beginPath(); g.moveTo(pts[0]!.x, pts[0]!.y);
        for (let i = 1; i < upto; i++) g.lineTo(pts[i]!.x, pts[i]!.y);
        g.strokePath();
        g.lineStyle(1, 0x6a5440, 0.6);
        g.beginPath(); g.moveTo(pts[0]!.x + 1, pts[0]!.y + 1);
        for (let i = 1; i < upto; i++) g.lineTo(pts[i]!.x + 1, pts[i]!.y + 1);
        g.strokePath();
      }
    };
    if (this.q.still) { draw(1); this.host.scene.time.delayedCall(ms, () => g.destroy()); return; }
    const state = { k: 0 };
    this.host.scene.tweens.add({ targets: state, k: 1, duration: ms * 0.3, onUpdate: () => draw(state.k) });
    this.host.scene.tweens.add({ targets: this.gone(g), alpha: 0, delay: ms * 0.55, duration: ms * 0.45, onComplete: () => g.destroy() });
  }

  /** The eagle's scream: rings of sound rolling out of its beak toward the foe, its eye flashing. */
  private screech(local: Pt | undefined, ms: number): void {
    const { from, to } = this.aim(local);
    const side = this.side;
    const scene = this.host.scene;
    this.flare(this.spark, 1, 500);
    this.glint(this.onArt(EYE.eagle ?? this.look.head), 0xffe060, ms * 0.6);
    const dir = Math.atan2(to.y - from.y, to.x - from.x);
    for (let i = 0; i < 4; i++) scene.time.delayedCall(i * 110, () => this.wave(from, dir, side * (0.5 + i * 0.25), side * (2.2 + i * 0.4), 520));
    this.ring(from.x, from.y, side * 2.4, 0xffffff, 560);
    scene.time.delayedCall(Math.max(200, ms * 0.35), () => { this.flash(to.x, to.y, side * 1.0, 0xe8f0ff, 360); this.ring(to.x, to.y, side * 1.4, 0xffffff, 420); });
    this.shake(320, 0.003);
    playSfx('spell.thunder_hit', { space: hugeSpace(this.host.size) });
  }

  /** One wave of sound: an arc opening out from a point in a direction, fading as it goes. */
  private wave(at: Pt, dir: number, from: number, to: number, ms: number): void {
    if (this.q.still) return;
    const g = this.host.scene.add.graphics({ x: at.x, y: at.y }).setBlendMode(ADD);
    this.host.container.add(g);
    const st = { r: from };
    const draw = () => {
      g.clear();
      g.lineStyle(2, 0xffffff, 0.8);
      g.beginPath(); g.arc(0, 0, st.r, dir - 0.6, dir + 0.6); g.strokePath();
      g.lineStyle(1, 0xd8e8ff, 0.5);
      g.beginPath(); g.arc(0, 0, st.r * 0.86, dir - 0.5, dir + 0.5); g.strokePath();
    };
    draw();
    this.host.scene.tweens.add({ targets: st, r: to, duration: ms, ease: 'Quad.easeOut', onUpdate: draw });
    this.host.scene.tweens.add({ targets: this.gone(g), alpha: 0, duration: ms, ease: 'Quad.easeIn', onComplete: () => g.destroy() });
  }

  /** A streak of air from one point to another (a stoop), drawn on fast and fading. */
  private streak(from: Pt, to: Pt, colour: number, ms: number): void {
    if (this.q.still) return;
    const g = this.host.scene.add.graphics().setBlendMode(ADD);
    this.host.container.add(g);
    const st = { k: 0 };
    const nx = -(to.y - from.y), ny = to.x - from.x, nl = Math.max(1, Math.hypot(nx, ny));
    const draw = () => {
      g.clear();
      const x = from.x + (to.x - from.x) * st.k, y = from.y + (to.y - from.y) * st.k;
      for (const [o, w, a] of [[0, 3, 0.9], [4, 1, 0.5], [-4, 1, 0.5]] as const) {
        g.lineStyle(w, colour, a);
        g.beginPath(); g.moveTo(from.x + (nx / nl) * o, from.y + (ny / nl) * o); g.lineTo(x + (nx / nl) * o * 0.3, y + (ny / nl) * o * 0.3); g.strokePath();
      }
    };
    this.host.scene.tweens.add({ targets: st, k: 1, duration: ms, ease: 'Quad.easeIn', onUpdate: draw });
    this.host.scene.tweens.add({ targets: this.gone(g), alpha: 0, delay: ms, duration: 320, onComplete: () => g.destroy() });
  }

  /** Roots bursting up round a point: they curl in over it and grip, then sink away. */
  private roots(at: Pt, r: number, ms: number): void {
    const g = this.host.scene.add.graphics({ x: at.x, y: at.y });
    this.host.container.add(g);
    const n = 7;
    let seed = this.host.unitId * 131 + Math.round(this.time);
    const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
    const arms = Array.from({ length: n }, (_, i) => {
      const a = (i / n) * Math.PI * 2 + rnd() * 0.5;
      return { x: Math.cos(a) * r * (0.8 + rnd() * 0.3), y: Math.sin(a) * r * 0.45, h: r * (0.7 + rnd() * 0.5), curl: (rnd() - 0.5) * 0.6 };
    });
    const draw = (k: number) => {
      g.clear();
      for (const a of arms) {
        // from the ground at its root, up and over toward the middle
        const steps = 6;
        const pts: Pt[] = [];
        for (let s = 0; s <= steps * k; s++) {
          const u = s / steps;
          pts.push({ x: a.x * (1 - u * 0.85) + a.curl * r * Math.sin(u * 3), y: a.y * (1 - u) - Math.sin(u * Math.PI * 0.9) * a.h });
        }
        if (pts.length < 2) continue;
        for (const [w, c] of [[4, 0x2a1a0c], [2, 0x7a5430]] as const) {
          g.lineStyle(w, c, 1);
          g.beginPath(); g.moveTo(pts[0]!.x, pts[0]!.y);
          for (const p of pts.slice(1)) g.lineTo(p.x, p.y);
          g.strokePath();
        }
        g.fillStyle(0x6a9a2a, 1); g.fillRect(pts[pts.length - 1]!.x - 1, pts[pts.length - 1]!.y - 1, 2, 2);
      }
    };
    if (this.q.still) { draw(1); this.host.scene.time.delayedCall(ms, () => g.destroy()); return; }
    const st = { k: 0 };
    this.host.scene.tweens.add({ targets: st, k: 1, duration: Math.max(200, ms * 0.25), ease: 'Back.easeOut', onUpdate: () => draw(Math.min(1, st.k)) });
    this.host.scene.tweens.add({ targets: this.gone(g), alpha: 0, delay: ms * 0.65, duration: ms * 0.35, onComplete: () => g.destroy() });
  }

  /** A clump of thorny brush springing up at a point and withering away. */
  private bramble(at: Pt, ms: number): void {
    if (this.q.still) return;
    const g = this.host.scene.add.graphics({ x: at.x, y: at.y });
    this.host.container.add(g);
    const s = this.side * 0.22;
    let seed = Math.round(at.x * 31 + at.y * 17 + this.host.unitId);
    const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
    for (let i = 0; i < 5; i++) {
      const bx = (rnd() - 0.5) * s * 1.4, lean = (rnd() - 0.5) * s, hh = s * (0.6 + rnd() * 0.8);
      g.lineStyle(2, i % 2 ? 0x3a5a1a : 0x4a3a1a, 1);
      g.beginPath(); g.moveTo(bx, 0); g.lineTo(bx + lean * 0.5, -hh * 0.5); g.lineTo(bx + lean, -hh); g.strokePath();
      // thorns along it
      g.lineStyle(1, 0xc8b878, 1);
      for (const k of [0.35, 0.7]) {
        const tx = bx + lean * k, ty = -hh * k;
        g.beginPath(); g.moveTo(tx, ty); g.lineTo(tx + (i % 2 ? 2 : -2), ty - 1); g.strokePath();
      }
    }
    g.setScale(1, 0.1);
    this.host.scene.tweens.add({ targets: g, scaleY: 1, duration: 160, ease: 'Back.easeOut' });
    this.host.scene.tweens.add({ targets: this.gone(g), alpha: 0, delay: ms * 0.6, duration: ms * 0.4, onComplete: () => g.destroy() });
    this.burst(TEX.thorn, at.x, at.y, 4, { speed: { min: 20, max: 50 }, angle: { min: 240, max: 300 }, gravityY: 160, lifespan: { min: 300, max: 500 }, tint: [0x4a6a24, 0x6a5a2a] });
  }

  /** A log flung end over end along an arc from one point to another. */
  private hurlLog(from: Pt, to: Pt, ms: number): void {
    if (this.q.still) return;
    const g = this.host.scene.add.graphics({ x: from.x, y: from.y });
    this.host.container.add(g);
    const len = this.side * 0.5, w = 4;
    g.fillStyle(0x2a1a0c, 1); g.fillRect(-len / 2 - 1, -w / 2 - 1, len + 2, w + 2);
    g.fillStyle(0x7a5430, 1); g.fillRect(-len / 2, -w / 2, len, w);
    g.fillStyle(0x9a7444, 1); g.fillRect(-len / 2, -w / 2, len, 1);
    g.fillStyle(0xc8a070, 1); g.fillRect(-len / 2, -1, 2, 2); g.fillRect(len / 2 - 2, -1, 2, 2);
    g.fillStyle(0x6a9a2a, 1); g.fillRect(len / 2 - 3, -w / 2 - 2, 3, 2);
    const st = { k: 0 };
    const peak = this.side * 1.2;
    this.gone(g);
    this.host.scene.tweens.add({
      targets: st, k: 1, duration: ms, ease: 'Linear',
      onUpdate: () => { g.setPosition(from.x + (to.x - from.x) * st.k, from.y + (to.y - from.y) * st.k - Math.sin(st.k * Math.PI) * peak); g.setRotation(st.k * Math.PI * 2.5 * (to.x < from.x ? -1 : 1)); },
      onComplete: () => g.destroy(),
    });
  }

  /** A wide arc swept in front of it (a club swung flat), its trail fading. */
  private arc(at: Pt, r: number, dir: number, swing: number, ms: number): void {
    if (this.q.still) return;
    const g = this.host.scene.add.graphics({ x: at.x, y: at.y }).setBlendMode(ADD);
    this.host.container.add(g);
    // from high behind its shoulder, round and across in front of it
    const a0 = dir > 0 ? -Math.PI * 0.75 : -Math.PI * 0.25, sweep = dir > 0 ? Math.PI * 1.1 : -Math.PI * 1.1;
    const st = { k: 0 };
    const draw = () => {
      g.clear();
      const a1 = a0 + sweep * st.k;
      const lo = Math.min(a0, a1), hi = Math.max(a0, a1);
      g.lineStyle(5, RUNE, 0.35); g.beginPath(); g.arc(0, 0, r, lo, hi); g.strokePath();
      g.lineStyle(2, 0xffffff, 0.85); g.beginPath(); g.arc(0, 0, r, Math.max(lo, hi - 0.6), hi); g.strokePath();
    };
    g.setScale(1, 0.6);
    this.host.scene.tweens.add({ targets: st, k: 1, duration: swing, ease: 'Quad.easeIn', onUpdate: draw });
    this.host.scene.tweens.add({ targets: this.gone(g), alpha: 0, delay: swing, duration: Math.max(200, ms * 0.4), onComplete: () => g.destroy() });
  }

  /** Stars wheeling over a staggered foe's head. */
  private stars(at: Pt, ms: number): void {
    if (this.q.still) return;
    const scene = this.host.scene;
    const n = 3, r = this.side * 0.22;
    const st = { a: 0 };
    const stars = Array.from({ length: n }, () => {
      const s = this.gone(scene.add.image(at.x, at.y, TEX.star).setTint(0xfff27a).setBlendMode(ADD));
      this.host.container.add(s);
      return s;
    });
    const place = () => stars.forEach((s, i) => { const a = st.a + (i / n) * Math.PI * 2; s.setPosition(at.x + Math.cos(a) * r, at.y - this.side * 0.45 + Math.sin(a) * r * 0.35); });
    place();
    scene.tweens.add({ targets: st, a: Math.PI * 4, duration: ms, onUpdate: place });
    scene.tweens.add({ targets: stars, alpha: 0, delay: ms * 0.7, duration: ms * 0.3, onComplete: () => stars.forEach((s) => s.destroy()) });
  }

  /** A crack glowing with cold light, running along the ground from one point to another, then closing. */
  private fissure(from: Pt, to: Pt, run: number, ms: number): void {
    const g = this.host.scene.add.graphics();
    this.under(g);
    const glow = this.host.scene.add.graphics().setBlendMode(ADD);
    this.host.container.add(glow);
    let seed = this.host.unitId * 53 + Math.round(this.time);
    const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
    const steps = 12;
    const nx = -(to.y - from.y), ny = to.x - from.x, nl = Math.max(1, Math.hypot(nx, ny));
    const pts: Pt[] = Array.from({ length: steps + 1 }, (_, i) => {
      const k = i / steps, j = i === 0 || i === steps ? 0 : (rnd() - 0.5) * this.side * 0.22;
      return { x: from.x + (to.x - from.x) * k + (nx / nl) * j, y: from.y + (to.y - from.y) * k + (ny / nl) * j * 0.5 };
    });
    const draw = (k: number) => {
      g.clear(); glow.clear();
      const upto = Math.max(1, Math.ceil(steps * k));
      g.lineStyle(3, 0x0e1218, 0.9);
      g.beginPath(); g.moveTo(pts[0]!.x, pts[0]!.y);
      for (let i = 1; i <= upto; i++) g.lineTo(pts[i]!.x, pts[i]!.y);
      g.strokePath();
      for (const [w, c, a] of [[3, RUNE, 0.45], [1, 0xffffff, 0.9]] as const) {
        glow.lineStyle(w, c, a);
        glow.beginPath(); glow.moveTo(pts[0]!.x, pts[0]!.y);
        for (let i = 1; i <= upto; i++) glow.lineTo(pts[i]!.x, pts[i]!.y);
        glow.strokePath();
      }
    };
    if (this.q.still) { draw(1); this.host.scene.time.delayedCall(ms, () => { g.destroy(); glow.destroy(); }); return; }
    const st = { k: 0 };
    this.host.scene.tweens.add({ targets: st, k: 1, duration: run, ease: 'Quad.easeOut', onUpdate: () => draw(st.k) });
    this.host.scene.tweens.add({ targets: this.gone(glow), alpha: 0, delay: run + ms * 0.2, duration: ms * 0.4, onComplete: () => glow.destroy() });
    this.host.scene.tweens.add({ targets: this.gone(g), alpha: 0, delay: run + ms * 0.4, duration: ms * 0.4, onComplete: () => g.destroy() });
  }

  /** Runes flaring one after another in a ring round its feet, then fading. */
  private runeRing(r: number, ms: number): void {
    if (this.q.still) return;
    const scene = this.host.scene;
    const n = 8;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const img = scene.add.image(Math.cos(a) * r, -this.side * 0.45 + Math.sin(a) * r * 0.5, TEX.rune).setTint(RUNE).setBlendMode(ADD).setAlpha(0);
      img.setDisplaySize(this.side * 0.24, this.side * 0.24);
      this.host.container.add(img);
      scene.tweens.add({ targets: this.gone(img), alpha: 1, y: img.y - 4, delay: i * Math.max(30, ms * 0.05), duration: Math.max(120, ms * 0.2), yoyo: true, hold: ms * 0.5, ease: 'Quad.easeOut', onComplete: () => img.destroy() });
    }
  }

  /** docs/116: water thrown up at a point — a sheet of spray and foam (`dark`: the maw's black water). */
  private splash(x: number, y: number, power: number, dark = false): void {
    this.burst(TEX.drop, x, y, Math.round(16 * power), {
      speed: { min: 50 * power, max: 120 * power }, angle: { min: 230, max: 310 }, gravityY: 300, lifespan: { min: 450, max: 800 }, scale: { start: 1, end: 0.6 }, tint: dark ? [0x2a1a3a, 0x4a3a5a, 0x8a6aa0] : [...FOAM, ...BRINE],
    });
    this.burst(TEX.px, x, y, Math.round(10 * power), { speed: { min: 20, max: 60 }, angle: { min: 180, max: 360 }, gravityY: 120, lifespan: { min: 300, max: 600 }, tint: dark ? [0x5a4a6a, 0x8a6aa0] : FOAM });
    this.ring(x, y, this.side * 1.2 * power, dark ? 0x2a1a3a : 0xe8f8fa, 480);
  }

  /** Bubbles boiling up from a point and bursting. */
  private bubbles(x: number, y: number, n: number): void {
    this.burst(TEX.dot, x, y, n, {
      speedX: { min: -10, max: 10 }, speedY: { min: -60, max: -20 }, lifespan: { min: 700, max: 1300 }, scale: { start: 0.5, end: 1 }, alpha: { start: 0.85, end: 0, ease: 'Expo.easeIn' },
      tint: [0xd8f4ff, 0xa8d8ec, 0x7ab8d0], emitZone: this.zone(-this.side * 0.6, -this.side * 0.2, this.side * 1.2, this.side * 0.4),
    });
  }

  /** The maw's small eyes, flaring open over its back all at once. */
  private manyEyes(n: number, ms: number): void {
    const back = this.onArt(MAW_BACK);
    const side = this.side;
    this.burst(TEX.px, back.x, back.y, n, {
      speed: { min: 0, max: 4 }, lifespan: { min: Math.max(300, ms * 0.5), max: Math.max(500, ms) }, scale: { start: 1.4, end: 0.6 }, alpha: { start: 1, end: 0, ease: 'Expo.easeIn' }, color: EYES, blendMode: ADD,
      emitZone: this.zone(-side * 0.22, -side * 0.12, side * 0.46, side * 0.26),
    });
  }

  /** The kraken's arm tip nearest a point (the `nth` nearest), on the art as it is turned. */
  private armToward(to: Pt, nth: number): Pt {
    const tips = ARM_TIPS.map((t) => this.onArt(t)).sort((a, b) => Math.hypot(a.x - to.x, a.y - to.y) - Math.hypot(b.x - to.x, b.y - to.y));
    return tips[Math.min(nth, tips.length - 1)]!;
  }

  /**
   * An arm (or the maw's tongue) shooting from one point to another along a writhing curve, holding,
   * then reeling back in: a dark outline, its colour, and suckers along it in `rim`.
   */
  private tentacle(from: Pt, to: Pt, colour: number, rim: number, shoot: number, hold: number, width: number): void {
    const g = this.host.scene.add.graphics();
    this.host.container.add(g);
    const dx = to.x - from.x, dy = to.y - from.y;
    const len = Math.max(1, Math.hypot(dx, dy));
    const nx = -dy / len, ny = dx / len;
    const lift = Math.min(this.side * 0.6, len * 0.3);
    const st = { k: 0, t: 0 };
    const draw = () => {
      g.clear();
      const steps = 14;
      const pts: Pt[] = [];
      for (let i = 0; i <= steps; i++) {
        const u = (i / steps) * st.k;
        // an arc up over the water, and a writhe along it that travels toward the tip
        const bow = Math.sin(u / Math.max(0.01, st.k) * Math.PI) * lift;
        const writhe = Math.sin(u * 9 - st.t * 0.02) * this.side * 0.06 * u;
        pts.push({ x: from.x + dx * u + nx * writhe, y: from.y + dy * u - bow + ny * writhe });
      }
      for (const [w, c] of [[width + 2, 0x0a0608], [width, colour]] as const) {
        g.lineStyle(w, c, 1);
        g.beginPath(); g.moveTo(pts[0]!.x, pts[0]!.y);
        for (const p of pts.slice(1)) g.lineTo(p.x, p.y);
        g.strokePath();
      }
      // tapering to its tip: suckers (or the tongue's sheen) along the underside
      g.fillStyle(rim, 1);
      for (let i = 2; i < pts.length; i += 2) g.fillRect(pts[i]!.x - 0.5, pts[i]!.y + width * 0.25, 1, 1);
      const tip = pts[pts.length - 1]!;
      g.fillStyle(colour, 1); g.fillRect(tip.x - 1, tip.y - 1, 3, 3);
    };
    if (this.q.still) { st.k = 1; draw(); this.host.scene.time.delayedCall(hold, () => g.destroy()); return; }
    this.gone(g);
    this.host.scene.tweens.add({
      targets: st, k: 1, t: 400, duration: shoot, ease: 'Quad.easeOut', onUpdate: draw,
      onComplete: () => this.host.scene.tweens.add({ targets: st, k: 0, t: 800, delay: hold * 0.4, duration: hold * 0.6, ease: 'Quad.easeIn', onUpdate: draw, onComplete: () => g.destroy() }),
    });
  }

  /** The maw's jaws snapping shut on a point: two rows of teeth closing from above and below. */
  private jaws(at: Pt, snap: number, ms: number): void {
    if (this.q.still) return;
    const g = this.host.scene.add.graphics({ x: at.x, y: at.y });
    this.host.container.add(g);
    const w = this.side * 0.5;
    const st = { gap: this.side * 0.45 };
    const row = (y: number, down: 1 | -1) => {
      g.fillStyle(0x2a0a24, 0.9); g.fillRect(-w, y - (down > 0 ? 3 : 0), w * 2, 3);
      g.fillStyle(0xf0e4c8, 1);
      for (let i = 0; i < 7; i++) {
        const x = -w + (i + 0.5) * ((w * 2) / 7);
        g.fillTriangle(x - 2, y, x + 2, y, x, y + down * 5);
      }
    };
    const draw = () => { g.clear(); row(-st.gap, 1); row(st.gap, -1); };
    draw();
    this.host.scene.tweens.add({ targets: st, gap: 2, duration: snap, ease: 'Back.easeIn', onUpdate: draw });
    this.host.scene.tweens.add({ targets: this.gone(g), alpha: 0, delay: snap + ms * 0.15, duration: Math.max(200, ms * 0.35), onComplete: () => g.destroy() });
  }

  /** A whirlpool at a point: foam spiralling in and round, shrinking as it drains. */
  private whirl(at: Pt, r: number, colour: number, ms: number): void {
    if (this.q.still) return;
    const g = this.host.scene.add.graphics({ x: at.x, y: at.y });
    this.under(g);
    const arms = 3;
    const st = { a: 0 };
    // redrawn as it turns, so the spiral stays flat on the water
    const draw = () => {
      g.clear();
      g.fillStyle(0x0a2a3a, 0.5); g.fillEllipse(0, 0, r * 1.2, r * 0.6);
      for (let a = 0; a < arms; a++) {
        g.lineStyle(2, colour, 0.85);
        g.beginPath();
        for (let i = 0; i <= 20; i++) {
          const u = i / 20, ang = st.a + (a / arms) * Math.PI * 2 + u * Math.PI * 2.2, rr = r * (1 - u * 0.85);
          const x = Math.cos(ang) * rr, y = Math.sin(ang) * rr * 0.5;
          if (i) g.lineTo(x, y); else g.moveTo(x, y);
        }
        g.strokePath();
      }
    };
    draw();
    g.setScale(0.3);
    this.host.scene.tweens.add({ targets: g, scale: 1, duration: Math.max(160, ms * 0.2), ease: 'Back.easeOut' });
    this.host.scene.tweens.add({ targets: st, a: Math.PI * 3, duration: ms, ease: 'Quad.easeIn', onUpdate: draw });
    this.host.scene.tweens.add({ targets: this.gone(g), alpha: 0, scale: 0.2, delay: ms * 0.55, duration: ms * 0.45, onComplete: () => g.destroy() });
  }

  private shake(ms: number, intensity: number): void {
    if (this.q.still) return;
    this.host.scene.cameras.main.shake(ms, intensity);
  }

  destroy(): void {
    if (this.dead) return;
    this.dead = true;
    this.host.scene.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    for (const t of this.timers) t.remove();
    for (const p of this.parts) if (p.active) p.destroy();
    this.shader?.destroy();
    if (this.texKey && this.host.scene.textures.exists(this.texKey)) this.host.scene.textures.remove(this.texKey);
  }
}
